// Snapshot Wizard - Snapshot 2.0 guided flow (State/Direction + Jarvis conversations)

import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/useAuth';
import { hasGuestPrivileges } from '../utils/roles';
import { accountabilityGroupsAPI, snapshotsAPI, jarvisAPI, commitmentsAPI } from '../api/client';
import { ShareGroupPicker } from '../components/AccountabilityShare';
import ConfirmModal from '../components/ConfirmModal';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  Bot,
  Send,
  User as UserIcon,
  Save,
  Plus,
  X,
  Target,
  RefreshCw,
  Heart,
  Compass,
  AlertTriangle,
  Star,
  Ban,
  PenLine,
  RotateCcw,
} from 'lucide-react';
import toast from 'react-hot-toast';
import MicButton from '../components/MicButton';
import AutosizeTextarea from '../components/AutosizeTextarea';
import ProfileRequirementsForm from '../components/ProfileRequirementsForm';
import type { GrowGoalSuggestion } from '../api/client';
import type {
  SnapshotCategory, ChatMessage, Commitment, AreaHealthDefinition, Snapshot,
  ProfileRequirement,
} from '../types';
import {
  currentSnapshotMonth,
  parseSnapshotMonthParam,
  snapshotMonthLabel,
} from '../lib/snapshotMonths';
import {
  SCHEMA_STATE_DIRECTION,
  STATE_BREAKING,
  STATE_STRAINED,
  STATE_HEALTHY,
  STATE_THRIVING,
  DIRECTION_SLIPPING,
  DIRECTION_MAINTAINING,
  DIRECTION_GROWING,
  STATE_LABELS,
  DIRECTION_LABELS,
  OWNER_EXPERIENCE_OPTIONS,
  pulsePrompt,
  type OwnerExperience,
  ratingSortKey,
  isAttention,
  isCelebrate,
  stateColor,
  directionColor,
} from '../lib/snapshotFlags';

type WizardStep =
  | 'pulse'
  | 'prayer'
  | 'definitions'
  | 'foundation'
  | 'commitments_review'
  | 'rating'
  | 'commitments_new'
  | 'most_important'
  | 'review';

interface NewCommitmentData {
  // Set when the card is a goal that already exists (carried into a revised
  // Snapshot), so submitting updates it instead of creating a copy.
  commitment_id?: number;
  description: string;
  category: number | null;
  frequency: 'one_time' | 'daily' | 'weekly' | 'biweekly' | 'monthly';
  is_shared: boolean;
  shared_group_ids: number[];
  target_count: number;
  scheduling_mode: 'specific' | 'flexible';
}

interface CommitmentReviewStatus {
  id: number;
  status: 'completed' | 'in_progress' | 'cancelled';
  notes: string;
}

function snapshotDraftBackupKey(userId: number, month: string) {
  return `if-snapshot-draft:${userId}:${month}`;
}

function readDraftBackup(userId: number, month: string): { savedAt: number; snapshot: Snapshot } | null {
  try {
    const raw = window.localStorage.getItem(snapshotDraftBackupKey(userId, month));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { savedAt?: number; snapshot?: Snapshot };
    if (!parsed?.snapshot || typeof parsed.savedAt !== 'number') return null;
    return { savedAt: parsed.savedAt, snapshot: parsed.snapshot };
  } catch {
    return null;
  }
}

function writeDraftBackup(userId: number, month: string, snapshot: Snapshot) {
  try {
    window.localStorage.setItem(
      snapshotDraftBackupKey(userId, month),
      JSON.stringify({ savedAt: Date.now(), snapshot }),
    );
  } catch {
    // Private mode / quota — server save is still the source of truth.
  }
}

function clearDraftBackup(userId: number, month: string) {
  try {
    window.localStorage.removeItem(snapshotDraftBackupKey(userId, month));
  } catch {
    // ignore
  }
}

function startDraftConflict(error: unknown): { code?: string; snapshotId?: number } {
  const data = (error as { response?: { data?: Record<string, unknown> } })?.response?.data;
  if (!data) return {};
  return {
    code: typeof data.code === 'string' ? data.code : undefined,
    snapshotId: typeof data.snapshot_id === 'number' ? data.snapshot_id : undefined,
  };
}

function secondaryRatingLabel(categoryName: string): string {
  return categoryName === 'Parenting & Children'
    ? "Children's view (optional)"
    : "Spouse's view (optional)";
}

interface RatingData {
  category: number;
  state: number | null;
  direction: number | null;
  rating_text: string;
  spouse_state?: number | null;
  spouse_direction?: number | null;
  skipped: boolean;
  reflection_mode: RatingInputMode;
  description: string;
  conversation_transcript: ChatMessage[];
}

interface PreviousRating {
  state: number | null;
  state_label: string;
  direction: number | null;
  direction_label: string;
  skipped: boolean;
}

type RatingInputMode = 'jarvis' | 'notes' | '';

const REFLECTION_PROMPT = 'Describe why you gave it this rating.';
const MIN_REFLECTION_LENGTH = 10;
const MIN_REFLECTION_ALNUM_CHARS = 5;
const REFLECTION_TOO_SHORT_HINT =
  'Add a little more detail; punctuation alone does not count.';

function isMeaningfulReflection(value: string): boolean {
  const trimmed = value.trim();
  return (
    trimmed.length >= MIN_REFLECTION_LENGTH &&
    (trimmed.match(/[A-Za-z0-9]/g)?.length ?? 0) >= MIN_REFLECTION_ALNUM_CHARS
  );
}

function ratingOpener(category: string, state: number, direction: number): string {
  return `You chose ${STATE_LABELS[state]} and ${DIRECTION_LABELS[direction]} for ${category}. What is happening right now that best explains that combination?`;
}

function pulseOpener(choice: OwnerExperience): string {
  const label = OWNER_EXPERIENCE_OPTIONS.find((option) => option.value === choice)?.label ?? choice;
  return `You chose ${label} for your Entrepreneur Pulse. What is shaping that experience most right now?`;
}

function fallbackSummary(messages: ChatMessage[]): string {
  return messages
    .filter((message) => message.role === 'user')
    .map((message) => message.content.trim())
    .filter(Boolean)
    .join(' ')
    .slice(0, 500);
}

const STATE_BUTTONS = [
  { value: STATE_BREAKING, label: STATE_LABELS[STATE_BREAKING], active: 'bg-red-500 text-white border-red-500' },
  { value: STATE_STRAINED, label: STATE_LABELS[STATE_STRAINED], active: 'bg-orange-500 text-white border-orange-500' },
  { value: STATE_HEALTHY, label: STATE_LABELS[STATE_HEALTHY], active: 'bg-blue-500 text-white border-blue-500' },
  { value: STATE_THRIVING, label: STATE_LABELS[STATE_THRIVING], active: 'bg-green-500 text-white border-green-500' },
];

const DIRECTION_BUTTONS = [
  { value: DIRECTION_SLIPPING, label: DIRECTION_LABELS[DIRECTION_SLIPPING] },
  { value: DIRECTION_MAINTAINING, label: DIRECTION_LABELS[DIRECTION_MAINTAINING] },
  { value: DIRECTION_GROWING, label: DIRECTION_LABELS[DIRECTION_GROWING] },
];

export default function SnapshotWizard() {
  const { user } = useAuth();
  // Grow goals are members-only, so guests rate their areas and skip the grow goal steps.
  const isGuest = hasGuestPrivileges(user);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [categories, setCategories] = useState<SnapshotCategory[]>([]);
  const [currentStep, setCurrentStep] = useState<WizardStep>('pulse');
  const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Pre-snapshot data gate: while the member has unmet admin-published data
  // requirements they cannot start the wizard — we show "finish your profile
  // first" with the completion form instead (creation is also blocked server-side).
  const [unmetRequirements, setUnmetRequirements] = useState<ProfileRequirement[]>([]);

  // An existing active draft must be explicitly continued or deleted.
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [finalizedConflict, setFinalizedConflict] = useState<{ snapshotId: number; month: string } | null>(null);
  const [thisMonthSnapshots, setThisMonthSnapshots] = useState<Snapshot[]>([]);
  const [snapshotMonth, setSnapshotMonth] = useState(
    () => parseSnapshotMonthParam(searchParams.get('month')) || currentSnapshotMonth(),
  );
  const [editingSnapshotId, setEditingSnapshotId] = useState<number | null>(null);
  const [draftVersion, setDraftVersion] = useState(1);
  const [removedCommitmentIds, setRemovedCommitmentIds] = useState<number[]>([]);
  const [draftReady, setDraftReady] = useState(false);
  const [autosaveStatus, setAutosaveStatus] = useState<
    'idle' | 'saving' | 'saved' | 'error'
  >('idle');
  const autosaveInFlightRef = useRef(false);
  const queuedAutosaveRef = useRef<Record<string, unknown> | null>(null);
  const failedAutosaveRef = useRef<Record<string, unknown> | null>(null);
  const skipFirstAutosaveRef = useRef(true);

  // Entrepreneur Pulse + prayer + foundation
  const [ownerExperience, setOwnerExperience] = useState<OwnerExperience | ''>('');
  const [ownerExperienceNote, setOwnerExperienceNote] = useState('');
  const [ownerExperienceMode, setOwnerExperienceMode] = useState<RatingInputMode>('notes');
  const [ownerExperienceSummary, setOwnerExperienceSummary] = useState('');
  const [ownerExperienceTranscript, setOwnerExperienceTranscript] = useState<ChatMessage[]>([]);
  const [pulseChatInput, setPulseChatInput] = useState('');
  const [pulseChatLoading, setPulseChatLoading] = useState(false);
  const [previousOwnerExperience, setPreviousOwnerExperience] = useState<{
    choice: string; label: string; note: string; summary: string;
  } | null>(null);
  const [majorIssue, setMajorIssue] = useState('');
  // Opt-in (default none) to share the snapshot-derived prayer with AGs.
  const [sharePrayerGroupIds, setSharePrayerGroupIds] = useState<number[]>([]);
  const [shareSnapshotGroupIds, setShareSnapshotGroupIds] = useState<number[]>([]);
  const [purposeStatement, setPurposeStatement] = useState('');
  // Quarterly health definitions
  const [needsDefinitions, setNeedsDefinitions] = useState(false);
  const [confirmSkipBenchmarks, setConfirmSkipBenchmarks] = useState(false);
  const benchmarksContinued = searchParams.get('benchmarks') === 'continue';
  const [carriedForward, setCarriedForward] = useState(false);
  const [isFirstSnapshot, setIsFirstSnapshot] = useState(false);
  const [isFirstDefinitionSetup, setIsFirstDefinitionSetup] = useState(false);
  const [definitions, setDefinitions] = useState<Map<number, AreaHealthDefinition>>(new Map());
  const [previousSnapshotMonth, setPreviousSnapshotMonth] = useState<string | null>(null);
  const [previousRatings, setPreviousRatings] = useState<Map<number, PreviousRating>>(new Map());

  // Ratings
  const [ratings, setRatings] = useState<Map<number, RatingData>>(new Map());
  const [mostImportantCategory, setMostImportantCategory] = useState<number | null>(null);

  // Chat — member notes are the default; Jarvis is an optional helper.
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [chatConcluded, setChatConcluded] = useState(false);
  const [chatError, setChatError] = useState('');
  const [inputMode, setInputMode] = useState<RatingInputMode>('notes');
  const chatScrollRef = useRef<HTMLDivElement>(null);
  const ratingSectionRef = useRef<HTMLDivElement>(null);
  const chatRequestTokenRef = useRef(0);
  const activeCategoryIdRef = useRef<number | null>(null);

  // Commitments
  const [existingCommitments, setExistingCommitments] = useState<Commitment[]>([]);
  const [commitmentReviews, setCommitmentReviews] = useState<Map<number, CommitmentReviewStatus>>(new Map());
  const [newCommitments, setNewCommitments] = useState<NewCommitmentData[]>([]);
  const [goalSuggestions, setGoalSuggestions] = useState<GrowGoalSuggestion[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [goalModal, setGoalModal] = useState<NewCommitmentData | null>(null);

  // Grow-goal coaching chat (new-grow-goals step)
  const [coachMessages, setCoachMessages] = useState<ChatMessage[]>([]);
  const [coachInput, setCoachInput] = useState('');
  const [isCoachLoading, setIsCoachLoading] = useState(false);
  const coachStartedRef = useRef(false);
  const coachEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const requestedMonth = parseSnapshotMonthParam(searchParams.get('month'));
        const [categoriesData, commitmentsData, period, mySnapshots] = await Promise.all([
          snapshotsAPI.getCategories(),
          isGuest
            ? Promise.resolve([])
            : commitmentsAPI.getForReview().catch((error) => {
              console.error('Error loading grow goals to review:', error);
              return [];
            }),
          snapshotsAPI.getCurrentPeriod(requestedMonth),
          snapshotsAPI.getMySnapshots(),
        ]);

        setCategories(categoriesData);
        setExistingCommitments(commitmentsData);

        const targetMonth = requestedMonth || currentSnapshotMonth();
        setSnapshotMonth(targetMonth);
        const sameMonth = mySnapshots
          .filter((s) => s.month === targetMonth)
          .sort((a, b) => b.version - a.version);
        const activeDraft = sameMonth.find(
          (snapshot) => !snapshot.is_finalized && !snapshot.superseded_at,
        ) || null;
        setThisMonthSnapshots(activeDraft ? [activeDraft] : []);

        const initialReviews = new Map<number, CommitmentReviewStatus>();
        commitmentsData.forEach((c) => {
          initialReviews.set(c.id, { id: c.id, status: 'in_progress', notes: '' });
        });
        setCommitmentReviews(initialReviews);

        const defMap = new Map<number, AreaHealthDefinition>();
        period.definitions.forEach((d) => defMap.set(d.category, d));

        // The backend only returns categories that apply to this member's
        // profile — Marriage/Parenting are auto-hidden for members they don't
        // apply to, so there's nothing to pre-skip here. We still carry manual
        // skip choices forward from the member's previous snapshot.
        const initialRatings = new Map<number, RatingData>();
        categoriesData.forEach((cat) => {
          const skipped = !!defMap.get(cat.id)?.skipped;
          initialRatings.set(cat.id, {
            category: cat.id,
            state: null,
            direction: null,
            rating_text: '',
            spouse_state: cat.has_spouse_rating ? null : undefined,
            spouse_direction: cat.has_spouse_rating ? null : undefined,
            skipped,
            reflection_mode: 'notes',
            description: '',
            conversation_transcript: [],
          });
        });
        setRatings(initialRatings);

        setNeedsDefinitions(period.needs_definitions);
        setCarriedForward(period.carried_forward);
        setIsFirstSnapshot(period.is_first_snapshot);
        setIsFirstDefinitionSetup(period.is_first_definition_setup);
        setDefinitions(defMap);
        setPreviousSnapshotMonth(period.previous_snapshot_month);
        setPreviousOwnerExperience(period.previous_owner_experience);
        setPreviousRatings(new Map(
          period.previous_ratings.map((rating) => [rating.category, rating]),
        ));
        setUnmetRequirements(period.unmet_requirements ?? []);

        if (user?.purpose_statement) setPurposeStatement(user.purpose_statement);

        if ((period.unmet_requirements ?? []).length === 0) {
          const requestedId = Number(searchParams.get('draft'));
          let draft =
            Number.isFinite(requestedId) && requestedId > 0
              ? mySnapshots.find((snapshot) => snapshot.id === requestedId) || null
              : null;
          if (!draft && Number.isFinite(requestedId) && requestedId > 0) {
            try {
              draft = await snapshotsAPI.getSnapshot(requestedId);
            } catch {
              toast.error('That saved draft is no longer available.');
            }
          }

          const hydrateDraft = (snap: Snapshot) => {
            setEditingSnapshotId(snap.id);
            applySnapshotToWizard(snap, categoriesData);
            if (user?.id && snap.month) {
              const backup = readDraftBackup(user.id, snap.month);
              const serverUpdated = snap.updated_at ? Date.parse(snap.updated_at) : 0;
              if (backup && backup.savedAt > (Number.isFinite(serverUpdated) ? serverUpdated : 0)) {
                applySnapshotToWizard({ ...snap, ...backup.snapshot, id: snap.id }, categoriesData);
                toast('Restored unsaved changes from this device.');
              }
            }
            setDraftReady(true);
          };

          if (draft && !draft.is_finalized) {
            hydrateDraft(draft);
          } else if (activeDraft) {
            hydrateDraft(activeDraft);
            navigate(
              `/snapshots/new?draft=${activeDraft.id}&month=${activeDraft.month}`,
              { replace: true },
            );
          } else {
            try {
              const created = await snapshotsAPI.startDraft(false, requestedMonth);
              hydrateDraft(created);
              navigate(
                `/snapshots/new?draft=${created.id}&month=${created.month}`,
                { replace: true },
              );
            } catch (startError) {
              const conflict = startDraftConflict(startError);
              if (conflict.code === 'month_already_finalized' && conflict.snapshotId) {
                setFinalizedConflict({
                  snapshotId: conflict.snapshotId,
                  month: requestedMonth || targetMonth,
                });
                setDraftReady(false);
              } else {
                throw startError;
              }
            }
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
    // Initialization intentionally runs once per authenticated member.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    const chat = chatScrollRef.current;
    if (chat) chat.scrollTo({ top: chat.scrollHeight, behavior: 'smooth' });
  }, [chatMessages]);

  useEffect(() => {
    coachEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [coachMessages]);

  // Load structured suggestions once; coaching remains available below.
  useEffect(() => {
    if (currentStep !== 'commitments_new' || coachStartedRef.current) return;
    coachStartedRef.current = true;
    void loadGoalSuggestions();
    void startCoach();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  const hasCommitmentsToReview = existingCommitments.length > 0;

  // Marriage/Parenting are auto-hidden by the backend for members they don't
  // apply to. Surface a gentle nudge so a member whose situation changed knows
  // how to bring those sections back.
  const marriageHidden = !!user && (user.family_status || '').toLowerCase() !== 'married';
  const parentingHidden = !!user && user.has_children === false;
  const profileSectionsHidden = marriageHidden || parentingHidden;

  // Ordered macro steps for this run. The prayer ("Major Prayer Request") step
  // is intentionally near the END — the June 25 stakeholder guidance is "fill out
  // the prayer last, but read it first": members think through every area before
  // naming what to pray for, while forum-day views (Review + SnapshotDetail) lead
  // with it. Progress math below is position-independent, so the placement is safe.
  const stepOrder: WizardStep[] = [
    'pulse',
    ...(needsDefinitions ? (['definitions'] as WizardStep[]) : []),
    'foundation',
    ...(hasCommitmentsToReview ? (['commitments_review'] as WizardStep[]) : []),
    'rating',
    ...(isGuest ? [] : (['commitments_new'] as WizardStep[])),
    'most_important',
    'prayer',
    'review',
  ];

  const currentCategory = categories[currentCategoryIndex];
  const currentRating = currentCategory ? ratings.get(currentCategory.id) : null;
  const currentDefinition = currentCategory ? definitions.get(currentCategory.id) : undefined;
  const previousRating = currentCategory ? previousRatings.get(currentCategory.id) : undefined;
  activeCategoryIdRef.current = currentCategory?.id ?? null;

  // --- definitions helpers ---
  const updateDefinition = (categoryId: number, patch: Partial<AreaHealthDefinition>) => {
    setDefinitions((prev) => {
      const next = new Map(prev);
      const existing = next.get(categoryId) || {
        category: categoryId,
        category_name: categories.find((c) => c.id === categoryId)?.name || '',
        healthy_looks_like: '',
        positive_indicators: [],
        slipping_signals: [],
      };
      next.set(categoryId, { ...existing, ...patch });
      return next;
    });
  };

  // --- rating helpers ---
  const updateRating = (patch: Partial<RatingData>) => {
    if (!currentCategory) return;
    updateRatingById(currentCategory.id, patch);
  };

  const updateRatingById = (categoryId: number, patch: Partial<RatingData>) => {
    setRatings((prev) => {
      const next = new Map(prev);
      const existing = next.get(categoryId);
      if (existing) next.set(categoryId, { ...existing, ...patch });
      return next;
    });
  };

  // --- chat ---
  const sendMessage = async (retryMessage?: string, retryHistory?: ChatMessage[]) => {
    const text = (retryMessage ?? chatInput).trim();
    if (!text || !currentCategory || !currentRating || isChatLoading) return;
    const categoryId = currentCategory.id;
    const requestToken = ++chatRequestTokenRef.current;
    const isCurrentRequest = () =>
      activeCategoryIdRef.current === categoryId &&
      chatRequestTokenRef.current === requestToken;
    const isRetry = retryHistory !== undefined;
    const updatedMessages = retryHistory ?? [
      ...chatMessages,
      { role: 'user' as const, content: text },
    ];
    if (!isRetry) {
      setChatMessages(updatedMessages);
      setChatInput('');
      updateRatingById(categoryId, { conversation_transcript: updatedMessages });
    }
    setIsChatLoading(true);
    setChatError('');
    try {
      const response = await jarvisAPI.chatAboutRating(
        categoryId,
        { state: currentRating.state ?? undefined, direction: currentRating.direction ?? undefined },
        text,
        updatedMessages,
      );
      if (!isCurrentRequest()) return;
      const completedMessages = [
        ...updatedMessages,
        { role: 'assistant' as const, content: response.response },
      ];
      setChatMessages(completedMessages);
      updateRatingById(categoryId, { conversation_transcript: completedMessages });
      if (response.is_complete) void concludeConversation(categoryId, completedMessages);
    } catch (error) {
      if (!isCurrentRequest()) return;
      console.error('Error sending message:', error);
      setChatError('Ask IF could not respond. Your answer is saved.');
    } finally {
      if (isCurrentRequest()) setIsChatLoading(false);
    }
  };

  const retryConversation = () => {
    const lastMessage = chatMessages.at(-1);
    if (lastMessage?.role === 'user') {
      sendMessage(lastMessage.content, chatMessages);
    }
  };

  // Member-initiated wrap-up: end the probe now and save the summary so far.
  const finishConversation = async () => {
    if (isChatLoading) return;
    if (!currentCategory) return;
    await concludeConversation(currentCategory.id, chatMessages);
  };

  const concludeConversation = async (categoryId: number, messages: ChatMessage[]) => {
    const rating = ratings.get(categoryId);
    if (!rating || !messages.some((message) => message.role === 'user')) return;
    const immediateSummary = rating.description || fallbackSummary(messages);
    updateRatingById(categoryId, {
      description: immediateSummary,
      conversation_transcript: messages,
    });
    if (activeCategoryIdRef.current === categoryId) setChatConcluded(true);
    try {
      const summaryResponse = await jarvisAPI.generateRatingSummary(
        categoryId,
        { state: rating.state ?? undefined, direction: rating.direction ?? undefined },
        messages,
      );
      if (summaryResponse.summary) {
        updateRatingById(categoryId, { description: summaryResponse.summary });
      }
    } catch (error) {
      console.error('Error saving conversation:', error);
    }
  };

  const persistChat = () => {
    if (!currentCategory || !chatMessages.some((message) => message.role === 'user')) return;
    void concludeConversation(currentCategory.id, chatMessages);
  };

  const resetChat = () => {
    chatRequestTokenRef.current += 1;
    setIsChatLoading(false);
    setChatMessages([]);
    setChatInput('');
    setChatConcluded(false);
    setChatError('');
  };

  const toggleSkip = () => {
    if (!currentCategory || !currentRating) return;
    const nextSkipped = !currentRating.skipped;
    updateRating(
      nextSkipped
        ? {
            skipped: true,
            state: null,
            direction: null,
            spouse_state: currentCategory.has_spouse_rating ? null : undefined,
            spouse_direction: currentCategory.has_spouse_rating ? null : undefined,
          }
        : { skipped: false },
    );
    if (nextSkipped) resetChat();
  };

  const switchToNotes = () => {
    if (isChatLoading) return;
    persistChat();
    setInputMode('notes');
    updateRating({ reflection_mode: 'notes' });
  };

  const switchToJarvis = () => {
    if (isChatLoading) return;
    if (!currentRating || currentRating.state == null || currentRating.direction == null) {
      toast.error('Choose both State and Direction before talking with Ask IF.');
      return;
    }
    setInputMode('jarvis');
    const existing = currentRating.conversation_transcript;
    const messages = existing.length > 0
      ? existing
      : [{
          role: 'assistant' as const,
          content: ratingOpener(
            currentCategory!.name,
            currentRating.state,
            currentRating.direction,
          ),
        }];
    setChatMessages(messages);
    updateRating({
      reflection_mode: 'jarvis',
      conversation_transcript: messages,
    });
  };

  // Load the area's saved transcript and default to member notes whenever the active
  // rating area changes.
  useEffect(() => {
    if (currentStep !== 'rating' || !currentCategory) return;
    chatRequestTokenRef.current += 1;
    setIsChatLoading(false);
    const savedRating = ratings.get(currentCategory.id);
    const nextMode = savedRating?.reflection_mode || 'notes';
    setInputMode(nextMode);
    if (savedRating && !savedRating.reflection_mode) {
      updateRatingById(currentCategory.id, { reflection_mode: 'notes' });
    }
    setChatMessages(savedRating?.conversation_transcript ?? []);
    setChatInput('');
    setChatConcluded(false);
    setChatError('');
    requestAnimationFrame(() => {
      ratingSectionRef.current?.scrollIntoView({ behavior: 'auto', block: 'start' });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, currentCategoryIndex]);

  // Recompute only the untouched deterministic opener when the rating changes.
  useEffect(() => {
    if (currentStep !== 'rating' || inputMode !== 'jarvis' || !currentCategory) return;
    if (currentRating?.skipped) return;
    if (currentRating?.state == null || currentRating.direction == null) return;
    if (
      chatMessages.length === 1
      && chatMessages[0].role === 'assistant'
      && !currentRating.conversation_transcript.some((message) => message.role === 'user')
    ) {
      const opener = {
        role: 'assistant' as const,
        content: ratingOpener(currentCategory.name, currentRating.state, currentRating.direction),
      };
      setChatMessages([opener]);
      updateRatingById(currentCategory.id, { conversation_transcript: [opener] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, inputMode, currentCategoryIndex, currentRating?.state, currentRating?.direction]);

  const choosePulseMode = (mode: Exclude<RatingInputMode, ''>) => {
    setOwnerExperienceMode(mode);
    if (mode === 'jarvis' && ownerExperience) {
      const messages = ownerExperienceTranscript.length
        ? ownerExperienceTranscript
        : [{ role: 'assistant' as const, content: pulseOpener(ownerExperience) }];
      setOwnerExperienceTranscript(messages);
    }
  };

  useEffect(() => {
    if (
      ownerExperienceMode === 'jarvis'
      && ownerExperience
      && ownerExperienceTranscript.length === 1
      && ownerExperienceTranscript[0].role === 'assistant'
    ) {
      const next = pulseOpener(ownerExperience);
      if (ownerExperienceTranscript[0].content !== next) {
        setOwnerExperienceTranscript([{ role: 'assistant', content: next }]);
      }
    }
  }, [ownerExperience, ownerExperienceMode, ownerExperienceTranscript]);

  const concludePulse = async (messages: ChatMessage[]) => {
    if (!ownerExperience || !messages.some((message) => message.role === 'user')) return;
    setOwnerExperienceSummary((current) => current || fallbackSummary(messages));
    try {
      const response = await jarvisAPI.generatePulseSummary(ownerExperience, messages);
      if (response.summary) setOwnerExperienceSummary(response.summary);
    } catch (error) {
      console.error('Error generating Pulse reflection:', error);
    }
  };

  const sendPulseMessage = async () => {
    const text = pulseChatInput.trim();
    if (!text || !ownerExperience || pulseChatLoading) return;
    const updated = [
      ...ownerExperienceTranscript,
      { role: 'user' as const, content: text },
    ];
    setOwnerExperienceTranscript(updated);
    setPulseChatInput('');
    setPulseChatLoading(true);
    try {
      const response = await jarvisAPI.chatAboutPulse(ownerExperience, text, updated);
      const completed = [
        ...updated,
        { role: 'assistant' as const, content: response.response },
      ];
      setOwnerExperienceTranscript(completed);
      if (response.is_complete) void concludePulse(completed);
    } catch (error) {
      console.error('Error in Pulse conversation:', error);
      toast.error('Ask IF could not respond. Your answer is still saved.');
    } finally {
      setPulseChatLoading(false);
    }
  };

  // --- commitments ---
  const updateCommitmentReview = (id: number, status: CommitmentReviewStatus['status']) => {
    setCommitmentReviews((prev) => {
      const next = new Map(prev);
      const existing = next.get(id);
      if (existing) next.set(id, { ...existing, status });
      return next;
    });
  };

  const addNewCommitment = () => {
    setGoalModal({
      description: '',
      category: null,
      frequency: 'one_time',
      is_shared: false,
      shared_group_ids: [],
      target_count: 1,
      scheduling_mode: 'specific',
    });
  };

  const updateNewCommitment = (index: number, data: Partial<NewCommitmentData>) => {
    setNewCommitments((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], ...data };
      return updated;
    });
  };

  const removeNewCommitment = (index: number) => {
    const removedId = newCommitments[index]?.commitment_id;
    if (removedId) setRemovedCommitmentIds((prev) => [...prev, removedId]);
    setNewCommitments((prev) => prev.filter((_, i) => i !== index));
  };

  const openGoalSuggestion = (suggestion: GrowGoalSuggestion) => {
    setGoalModal({
      description: suggestion.description,
      category: categories.find((category) => category.name === suggestion.category_name)?.id ?? null,
      frequency: suggestion.frequency,
      is_shared: false,
      shared_group_ids: [],
      target_count: 1,
      scheduling_mode: 'specific',
    });
  };

  const confirmGoal = () => {
    if (!goalModal?.description.trim()) return;
    setNewCommitments((previous) => [...previous, goalModal]);
    setGoalModal(null);
    toast.success('Grow Goal added to this Snapshot.');
  };

  const loadGoalSuggestions = async () => {
    setSuggestionsLoading(true);
    try {
      const response = await jarvisAPI.commitmentSuggest(buildCoachContext());
      setGoalSuggestions(response.suggestions);
    } catch (error) {
      console.error('Error loading Grow Goal suggestions:', error);
      setGoalSuggestions([]);
    } finally {
      setSuggestionsLoading(false);
    }
  };

  // --- grow-goal coaching chat ---
  const buildCoachContext = () => ({
    categories: categories
      .filter((cat) => !ratings.get(cat.id)?.skipped)
      .map((cat) => {
        const r = ratings.get(cat.id);
        const d = definitions.get(cat.id);
        return {
          name: cat.name,
          state: r?.state ?? undefined,
          direction: r?.direction ?? undefined,
          rating_text: r?.rating_text || r?.description || '',
          healthy_looks_like: d?.healthy_looks_like || '',
          slipping_signals: d?.slipping_signals || [],
        };
      }),
    purpose_statement: purposeStatement,
    major_issue: majorIssue,
  });

  const startCoach = async () => {
    setIsCoachLoading(true);
    setCoachMessages([]);
    try {
      const { response } = await jarvisAPI.growGoalChat(buildCoachContext());
      setCoachMessages([{ role: 'assistant', content: response }]);
    } catch (error) {
      console.error('Error starting grow-goal coaching:', error);
      setCoachMessages([
        {
          role: 'assistant',
          content:
            "Let's turn your snapshot into a goal or two. Which area would you like to strengthen this month?",
        },
      ]);
    } finally {
      setIsCoachLoading(false);
    }
  };

  const sendCoachMessage = async () => {
    if (!coachInput.trim() || isCoachLoading) return;
    const userMessage: ChatMessage = { role: 'user', content: coachInput };
    const updated = [...coachMessages, userMessage];
    setCoachMessages(updated);
    setCoachInput('');
    setIsCoachLoading(true);
    try {
      const { response } = await jarvisAPI.growGoalChat(buildCoachContext(), userMessage.content, updated);
      setCoachMessages([...updated, { role: 'assistant', content: response }]);
    } catch (error) {
      console.error('Error in grow-goal coaching:', error);
      setCoachMessages([
        ...updated,
        { role: 'assistant', content: 'I had trouble responding. You can keep going or add a Grow Goal manually below.' },
      ]);
    } finally {
      setIsCoachLoading(false);
    }
  };

  const getFrequencyLabel = (frequency: string) => {
    const labels: Record<string, string> = {
      one_time: 'One-time',
      daily: 'Daily',
      weekly: 'Weekly',
      monthly: 'Monthly',
    };
    return labels[frequency] || frequency;
  };

  // --- navigation ---
  const goToMacro = (step: WizardStep, opts?: { lastCategory?: boolean }) => {
    resetChat();
    if (step === 'rating') {
      setCurrentCategoryIndex(opts?.lastCategory ? Math.max(categories.length - 1, 0) : 0);
    }
    setCurrentStep(step);
  };

  const nextFromMacro = () => {
    const i = stepOrder.indexOf(currentStep);
    if (i < stepOrder.length - 1) goToMacro(stepOrder[i + 1]);
  };

  const backFromMacro = () => {
    const i = stepOrder.indexOf(currentStep);
    if (i > 0) goToMacro(stepOrder[i - 1], { lastCategory: stepOrder[i - 1] === 'rating' });
  };

  const ratingNext = () => {
    if (isChatLoading) return;
    persistChat();
    if (currentCategoryIndex < categories.length - 1) {
      resetChat();
      setCurrentCategoryIndex(currentCategoryIndex + 1);
    } else {
      nextFromMacro();
    }
  };

  const ratingBack = () => {
    if (isChatLoading) return;
    persistChat();
    if (currentCategoryIndex > 0) {
      resetChat();
      setCurrentCategoryIndex(currentCategoryIndex - 1);
    } else {
      backFromMacro();
    }
  };

  useEffect(() => {
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: 'auto' });
      ratingSectionRef.current?.scrollIntoView({ block: 'start', behavior: 'auto' });
    });
  }, [currentStep, currentCategoryIndex]);

  // --- submit ---
  // Skipped (not-applicable) areas don't need a definition or a State.
  const definitionsComplete = categories.every(
    (c) => ratings.get(c.id)?.skipped || (definitions.get(c.id)?.healthy_looks_like || '').trim(),
  );
  const ratingsComplete = categories.every((c) => {
    const r = ratings.get(c.id);
    return (
      r?.skipped ||
      (
        r?.state != null
        && r.direction != null
        && (
          (r.reflection_mode === 'notes' && isMeaningfulReflection(r.rating_text))
          || (
            r.reflection_mode === 'jarvis'
            && isMeaningfulReflection(r.description)
            && r.conversation_transcript.some((message) => message.role === 'user')
          )
        )
      )
    );
  });
  const pulseComplete = !!ownerExperience && (
    (ownerExperienceMode === 'notes' && isMeaningfulReflection(ownerExperienceNote))
    || (
      ownerExperienceMode === 'jarvis'
      && isMeaningfulReflection(ownerExperienceSummary)
      && ownerExperienceTranscript.some((message) => message.role === 'user')
    )
  );

  const calculateProgress = () => {
    if (categories.length === 0) return 0;
    const ratingIndex = stepOrder.indexOf('rating');
    const total = ratingIndex + categories.length + (stepOrder.length - 1 - ratingIndex);
    const macroIndex = stepOrder.indexOf(currentStep);
    const current =
      macroIndex < ratingIndex
        ? macroIndex + 1
        : currentStep === 'rating'
          ? ratingIndex + currentCategoryIndex + 1
          : ratingIndex + categories.length + (macroIndex - ratingIndex);
    return Math.max(0, Math.min(99, Math.round((current / total) * 100)));
  };

  // Prefill the wizard from an existing active draft.
  const applySnapshotToWizard = (
    snap: Snapshot,
    availableCategories: SnapshotCategory[] = categories,
  ) => {
    if (snap.month) setSnapshotMonth(snap.month);
    setDraftVersion(snap.version ?? 1);
    setOwnerExperience((snap.owner_experience as OwnerExperience) || '');
    setOwnerExperienceNote(snap.owner_experience_note || '');
    setOwnerExperienceMode(snap.owner_experience_reflection_mode || 'notes');
    setOwnerExperienceSummary(snap.owner_experience_summary || '');
    setOwnerExperienceTranscript(snap.owner_experience_transcript || []);
    setMajorIssue(snap.major_issue || '');
    setSharePrayerGroupIds(snap.draft_data?.share_prayer_group_ids ?? []);
    setShareSnapshotGroupIds(snap.draft_data?.share_snapshot_group_ids ?? []);
    setPurposeStatement(snap.purpose_statement || '');
    setMostImportantCategory(snap.most_important_category);
    if (snap.draft_data?.commitment_reviews?.length) {
      setCommitmentReviews(new Map(
        snap.draft_data.commitment_reviews.map((review) => [review.id, review]),
      ));
    }
    setRemovedCommitmentIds(snap.draft_data?.removed_commitment_ids ?? []);
    if (snap.draft_data?.new_commitments) {
      setNewCommitments(snap.draft_data.new_commitments.map((commitment) => ({
        ...commitment,
        shared_group_ids: commitment.shared_group_ids ?? [],
        target_count: commitment.target_count ?? 1,
        scheduling_mode: commitment.scheduling_mode ?? 'specific',
      })));
    }
    setRatings((prev) => {
      const next = new Map(prev);
      snap.ratings.forEach((r) => {
        next.set(r.category, {
          category: r.category,
          state: r.state,
          direction: r.direction,
          rating_text: r.rating_text || '',
          spouse_state: r.spouse_state,
          spouse_direction: r.spouse_direction,
          skipped: r.skipped,
          reflection_mode: r.reflection_mode || 'notes',
          description: r.description || '',
          conversation_transcript: r.conversation_transcript || [],
        });
      });
      return next;
    });
    if (snap.area_definitions?.length) {
      setDefinitions((prev) => {
        const next = new Map(prev);
        snap.area_definitions.forEach((d) => next.set(d.category, d));
        return next;
      });
    }
    const validSteps: WizardStep[] = [
      'pulse', 'prayer', 'definitions', 'foundation', 'commitments_review',
      'rating', 'commitments_new', 'most_important', 'review',
    ];
    if (isGuest && snap.wizard_step === 'commitments_new') {
      setCurrentStep('most_important');
    } else if (validSteps.includes(snap.wizard_step as WizardStep)) {
      setCurrentStep(snap.wizard_step as WizardStep);
    }
    if (snap.wizard_category) {
      const categoryIndex = availableCategories.findIndex(
        (category) => category.id === snap.wizard_category,
      );
      if (categoryIndex >= 0) setCurrentCategoryIndex(categoryIndex);
    }
  };

  const handleEditCurrent = () => {
    const snap = thisMonthSnapshots[0];
    if (snap) {
      setEditingSnapshotId(snap.id);
      applySnapshotToWizard(snap);
      setDraftReady(true);
    }
    setDuplicateModalOpen(false);
  };

  const handleCreateNew = async () => {
    setIsSaving(true);
    try {
      const draft = await snapshotsAPI.startDraft(true, snapshotMonth);
      setEditingSnapshotId(draft.id);
      applySnapshotToWizard(draft);
      setDraftReady(true);
      setDuplicateModalOpen(false);
      navigate(`/snapshots/new?draft=${draft.id}&month=${draft.month}`, { replace: true });
    } catch {
      toast.error('Could not start a new Snapshot. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReviseFinalized = async () => {
    if (!finalizedConflict) return;
    setIsSaving(true);
    try {
      const draft = await snapshotsAPI.reviseSnapshot(finalizedConflict.snapshotId);
      setEditingSnapshotId(draft.id);
      applySnapshotToWizard(draft);
      setDraftReady(true);
      setFinalizedConflict(null);
      navigate(`/snapshots/new?draft=${draft.id}&month=${draft.month}`, { replace: true });
    } catch {
      toast.error('Could not start a new version. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleMoveToCurrentMonth = async () => {
    if (!editingSnapshotId) return;
    setIsSaving(true);
    try {
      const moved = await snapshotsAPI.moveToCurrentMonth(editingSnapshotId);
      if (user?.id) clearDraftBackup(user.id, snapshotMonth);
      setSnapshotMonth(moved.month);
      navigate(`/snapshots/new?draft=${moved.id}&month=${moved.month}`, { replace: true });
      toast.success(`Filed under ${snapshotMonthLabel(moved.month)}.`);
    } catch (err) {
      const message = (err as { response?: { data?: { error?: string } } }).response?.data?.error;
      toast.error(message || 'Could not move this draft. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const jumpTo = (value: string) => {
    if (value.startsWith('rating:')) {
      const index = Number(value.slice(7));
      if (Number.isFinite(index) && index >= 0) {
        resetChat();
        setCurrentCategoryIndex(index);
        setCurrentStep('rating');
      }
      return;
    }
    goToMacro(value as WizardStep);
  };

  const buildSnapshotPayload = (finalize: boolean): Record<string, unknown> => {
    const month = snapshotMonth;
    const definitionsPayload = Array.from(definitions.values())
      .filter(
        (definition) =>
          (definition.healthy_looks_like || '').trim() ||
          definition.positive_indicators.length ||
          definition.slipping_signals.length,
      )
      .map((definition) => ({
        category: definition.category,
        healthy_looks_like: definition.healthy_looks_like,
        positive_indicators: definition.positive_indicators,
        slipping_signals: definition.slipping_signals,
      }));

    return {
      month,
      schema_version: SCHEMA_STATE_DIRECTION,
      owner_experience: ownerExperience || '',
      owner_experience_note: ownerExperienceNote,
      owner_experience_reflection_mode: ownerExperienceMode,
      owner_experience_summary: ownerExperienceSummary,
      owner_experience_transcript: ownerExperienceTranscript,
      purpose_statement: purposeStatement,
      major_issue: majorIssue,
      share_prayer_request: sharePrayerGroupIds.length > 0,
      share_prayer_group_ids: sharePrayerGroupIds,
      most_important_category: mostImportantCategory,
      is_finalized: finalize,
      wizard_step: currentStep,
      wizard_progress: finalize ? 100 : calculateProgress(),
      wizard_category: currentStep === 'rating' ? currentCategory?.id ?? null : null,
      draft_data: {
        share_prayer: sharePrayerGroupIds.length > 0,
        share_prayer_group_ids: sharePrayerGroupIds,
        share_snapshot_group_ids: shareSnapshotGroupIds,
        commitment_reviews: Array.from(commitmentReviews.values()),
        new_commitments: newCommitments,
        removed_commitment_ids: removedCommitmentIds,
      },
      definitions: definitionsPayload,
      ratings: Array.from(ratings.values()).map((rating) => ({
        category: rating.category,
        state: rating.skipped ? null : rating.state,
        direction: rating.skipped ? null : rating.direction,
        rating_text: rating.rating_text,
        spouse_state: rating.skipped ? null : rating.spouse_state,
        spouse_direction: rating.skipped ? null : rating.spouse_direction,
        skipped: rating.skipped,
        reflection_mode: rating.reflection_mode,
        description: rating.description,
        conversation_transcript: rating.conversation_transcript,
      })),
    };
  };

  const persistAutosave = async (payload: Record<string, unknown>) => {
    if (!editingSnapshotId) return;
    if (autosaveInFlightRef.current) {
      queuedAutosaveRef.current = payload;
      return;
    }
    autosaveInFlightRef.current = true;
    setAutosaveStatus('saving');
    try {
      await snapshotsAPI.updateSnapshot(editingSnapshotId, payload);
      failedAutosaveRef.current = null;
      setAutosaveStatus('saved');
    } catch {
      failedAutosaveRef.current = payload;
      if (user?.id && snapshotMonth) {
        writeDraftBackup(user.id, snapshotMonth, {
          id: editingSnapshotId ?? 0,
          month: snapshotMonth,
          owner_experience: payload.owner_experience,
          owner_experience_note: payload.owner_experience_note,
          owner_experience_reflection_mode: payload.owner_experience_reflection_mode,
          owner_experience_summary: payload.owner_experience_summary,
          owner_experience_transcript: payload.owner_experience_transcript,
          major_issue: payload.major_issue,
          purpose_statement: payload.purpose_statement,
          most_important_category: payload.most_important_category,
          wizard_step: payload.wizard_step,
          wizard_category: payload.wizard_category,
          draft_data: payload.draft_data,
          ratings: payload.ratings,
          area_definitions: Array.from(definitions.values()),
        } as Snapshot);
      }
      setAutosaveStatus('error');
    } finally {
      autosaveInFlightRef.current = false;
      const queued = queuedAutosaveRef.current;
      queuedAutosaveRef.current = null;
      if (queued) void persistAutosave(queued);
    }
  };

  useEffect(() => {
    if (!draftReady || !editingSnapshotId || isSaving) return;
    if (skipFirstAutosaveRef.current) {
      skipFirstAutosaveRef.current = false;
      return;
    }
    const timer = window.setTimeout(() => {
      void persistAutosave(buildSnapshotPayload(false));
    }, 900);
    return () => window.clearTimeout(timer);
    // Every meaningful wizard field and transition is intentionally observed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    draftReady, editingSnapshotId, currentStep, currentCategoryIndex,
    ownerExperience, ownerExperienceNote, ownerExperienceMode,
    ownerExperienceSummary, ownerExperienceTranscript, majorIssue, sharePrayerGroupIds,
    shareSnapshotGroupIds,
    purposeStatement, definitions, ratings,
    mostImportantCategory, commitmentReviews, newCommitments,
  ]);

  const retryAutosave = () => {
    if (failedAutosaveRef.current) void persistAutosave(failedAutosaveRef.current);
  };

  const handleSubmit = async (finalize: boolean, allowWithoutBenchmarks = false) => {
    if (finalize && !pulseComplete) {
      toast.error('Complete your Entrepreneur Pulse reflection before finalizing.');
      setCurrentStep('pulse');
      return;
    }
    if (
      finalize
      && needsDefinitions
      && !definitionsComplete
      && !allowWithoutBenchmarks
      && !benchmarksContinued
    ) {
      setConfirmSkipBenchmarks(true);
      return;
    }
    if (finalize && !ratingsComplete) {
      toast.error(REFLECTION_PROMPT);
      setCurrentStep('rating');
      return;
    }
    setIsSaving(true);
    try {
      const payload = buildSnapshotPayload(finalize);

      // Edit the existing snapshot, or create a new version (V2) — explicit,
      // never a silent upsert overwrite.
      const snapshot = editingSnapshotId
        ? await snapshotsAPI.updateSnapshot(editingSnapshotId, payload)
        : await snapshotsAPI.createSnapshot(payload);

      const reviewPromises = Array.from(commitmentReviews.values()).map((review) => {
        if (review.status === 'completed') return commitmentsAPI.complete(review.id);
        if (review.status === 'cancelled') return commitmentsAPI.cancel(review.id);
        return Promise.resolve();
      });

      // Drafts keep their goals in draft_data; goals are only written on
      // submit, so saving a draft and submitting later doesn't copy them.
      const goalsToSave = finalize ? newCommitments.filter((c) => c.description.trim()) : [];
      const createPromises = goalsToSave.map((c) => {
        const fields = {
          description: c.description,
          category: c.category,
          frequency: c.frequency,
          is_shared: c.is_shared,
          target_count: c.target_count,
          scheduling_mode: c.scheduling_mode,
        };
        const saved = c.commitment_id
          ? commitmentsAPI.update(c.commitment_id, fields)
          : commitmentsAPI.create({ ...fields, snapshot: snapshot.id });
        return saved.then(async (created) => {
          if (c.shared_group_ids.length > 0) {
            await accountabilityGroupsAPI.setItemShares(
              'grow_goal',
              created.id,
              c.shared_group_ids,
            );
          }
          return created;
        });
      });

      const cancelPromises = finalize
        ? removedCommitmentIds.map((id) => commitmentsAPI.update(id, { status: 'cancelled' }))
        : [];

      const shareSnapshotPromises = finalize
        ? shareSnapshotGroupIds.map((groupId) =>
            accountabilityGroupsAPI.setShareSnapshot(groupId, true),
          )
        : [];

      void Promise.allSettled([
        ...reviewPromises,
        ...createPromises,
        ...cancelPromises,
        ...shareSnapshotPromises,
      ]).then((results) => {
        if (results.some((result) => result.status === 'rejected')) {
          toast.error('Snapshot saved, but a Grow Goal or share update needs retrying.');
        }
      });

      if (user?.id && snapshotMonth) clearDraftBackup(user.id, snapshotMonth);

      navigate('/snapshots', {
        replace: true,
        state: {
          snapshotNotice: finalize ? 'submitted' : 'draft',
        },
      });
    } catch (error) {
      console.error('Error saving snapshot:', error);
      toast.error('Failed to save snapshot. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy"></div>
      </div>
    );
  }

  // Pre-snapshot data gate: complete the requested info before starting.
  if (unmetRequirements.length > 0) {
    return (
      <div className="max-w-xl mx-auto space-y-6">
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 bg-navy/10 rounded-xl flex items-center justify-center flex-shrink-0">
              <UserIcon className="text-navy" size={24} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-navy">Let's finish your profile first</h1>
              <p className="text-gray-600 text-sm mt-1">
                Iron Forums needs a bit more info from you before you start your
                snapshot. It only takes a moment.
              </p>
            </div>
          </div>
          <ProfileRequirementsForm
            requirements={unmetRequirements}
            onCompleted={() => setUnmetRequirements([])}
          />
          <button
            onClick={() => navigate('/')}
            className="w-full mt-4 text-sm text-gray-500 hover:text-navy transition"
          >
            Back to dashboard
          </button>
        </div>
      </div>
    );
  }

  // Progress math: rating expands into one sub-step per category.
  const ratingIdx = stepOrder.indexOf('rating');
  const totalSteps = ratingIdx + categories.length + (stepOrder.length - 1 - ratingIdx);
  const macroIdx = stepOrder.indexOf(currentStep);
  let currentStepNumber: number;
  if (macroIdx < ratingIdx) {
    currentStepNumber = macroIdx + 1;
  } else if (currentStep === 'rating') {
    currentStepNumber = ratingIdx + currentCategoryIndex + 1;
  } else {
    currentStepNumber = ratingIdx + categories.length + (macroIdx - ratingIdx);
  }
  const progressPercent = (currentStepNumber / totalSteps) * 100;

  const stepTitle: Record<WizardStep, string> = {
    pulse: 'Entrepreneur Pulse',
    prayer: 'Major Prayer Request',
    definitions: 'Define Healthy',
    foundation: 'Foundation',
    commitments_review: 'Review Grow Goals',
    rating: currentCategory?.name || 'Ratings',
    commitments_new: 'New Grow Goals',
    most_important: 'Most Important Topic',
    review: 'Review & Submit',
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Existing draft choice */}
      {duplicateModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 space-y-4">
            <h2 className="text-xl font-bold text-navy">A Snapshot is already in progress</h2>
            <p className="text-gray-600">
              You already have a {snapshotMonthLabel(snapshotMonth)} Snapshot in progress.
              Starting new permanently deletes that month’s draft. You can still keep a
              draft for the other month.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                onClick={handleEditCurrent}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border-2 border-navy text-navy rounded-lg font-semibold hover:bg-navy hover:text-white transition"
              >
                <PenLine size={18} />
                Continue
              </button>
              <button
                onClick={handleCreateNew}
                disabled={isSaving}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-navy text-white rounded-lg font-semibold hover:bg-navy-dark transition"
              >
                <Plus size={18} />
                Start New
              </button>
            </div>
            <button
              onClick={() => navigate('/snapshots')}
              className="w-full text-sm text-gray-500 hover:text-navy transition"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {finalizedConflict && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 space-y-4">
            <h2 className="text-xl font-bold text-navy">Start a new version?</h2>
            <p className="text-gray-600">
              You already submitted {snapshotMonthLabel(finalizedConflict.month)}. A new
              version copies your answers so you can revise. The submitted snapshot stays.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                type="button"
                onClick={() => navigate('/snapshots')}
                className="flex-1 px-4 py-3 border-2 border-navy text-navy rounded-lg font-semibold hover:bg-navy hover:text-white transition"
              >
                Keep submitted
              </button>
              <button
                type="button"
                onClick={() => void handleReviseFinalized()}
                disabled={isSaving}
                className="flex-1 px-4 py-3 bg-navy text-white rounded-lg font-semibold hover:bg-navy-dark transition disabled:opacity-50"
              >
                Start new version
              </button>
            </div>
          </div>
        </div>
      )}

      {draftReady && editingSnapshotId && !isGuest && draftVersion === 1 && snapshotMonth < currentSnapshotMonth() && (
        <div className="rounded-xl border border-gold/40 bg-gold/10 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <p className="text-sm text-gray-700 flex-1">
            This draft is for {snapshotMonthLabel(snapshotMonth)}. File it under{' '}
            {snapshotMonthLabel(currentSnapshotMonth())} instead?
          </p>
          <button
            type="button"
            onClick={() => void handleMoveToCurrentMonth()}
            disabled={isSaving}
            className="px-4 py-2 bg-navy text-white rounded-lg text-sm font-semibold hover:bg-navy-dark transition disabled:opacity-50"
          >
            Move to {snapshotMonthLabel(currentSnapshotMonth())}
          </button>
        </div>
      )}

      {/* Progress Bar */}
      <div className="bg-white rounded-2xl p-4 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-600">
            Step {currentStepNumber} of {totalSteps}
          </span>
          <div className="text-right">
            <span className="block text-sm font-medium text-navy">{stepTitle[currentStep]}</span>
            <span
              className={`block text-xs ${
                autosaveStatus === 'error' ? 'text-red-600' : 'text-gray-400'
              }`}
              role="status"
            >
              {autosaveStatus === 'saving' && 'Saving…'}
              {autosaveStatus === 'saved' && 'Saved'}
              {autosaveStatus === 'idle' && 'Changes save automatically'}
              {autosaveStatus === 'error' && (
                <>
                  Save failed ·{' '}
                  <button type="button" onClick={retryAutosave} className="underline font-medium">
                    Retry
                  </button>
                </>
              )}
            </span>
          </div>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-navy h-2 rounded-full transition-all duration-300"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
        {draftReady && (
          <select
            id="snapshot-jump"
            aria-label="Jump to a page you already filled"
            value={currentStep === 'rating' ? `rating:${currentCategoryIndex}` : currentStep}
            onChange={(event) => jumpTo(event.target.value)}
            className="mt-3 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-navy focus:outline-none focus:ring-2 focus:ring-navy/20"
          >
            {stepOrder.flatMap((step) => (
              step === 'rating'
                ? categories.map((category, index) => (
                    <option key={`rating:${category.id}`} value={`rating:${index}`}>
                      {category.name}
                    </option>
                  ))
                : [
                    <option key={step} value={step}>
                      {stepTitle[step]}
                    </option>,
                  ]
            ))}
          </select>
        )}
      </div>

      {/* Entrepreneur Pulse Step */}
      {currentStep === 'pulse' && (
        <div className="bg-white rounded-2xl p-6 shadow-sm space-y-6">
          <div>
            <h1 className="text-2xl font-bold text-navy">
              {snapshotMonthLabel(snapshotMonth)} Snapshot
            </h1>
            <p className="text-gray-600 mt-1">
              {pulsePrompt(user?.snapshot_type)}
            </p>
          </div>

          {isFirstSnapshot && isFirstDefinitionSetup && needsDefinitions && (
            <div className="rounded-xl border border-gold/40 bg-gold/10 p-4">
              <p className="font-semibold text-navy">A quick note about your first Snapshot</p>
              <p className="text-sm text-gray-700 mt-1">
                You can describe what healthy looks like in each area, or continue without those
                benchmarks and fill them in later. Future Snapshots reuse whatever you set for the
                current season.
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {OWNER_EXPERIENCE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setOwnerExperience(opt.value)}
                className={`p-4 rounded-xl border-2 text-center font-semibold transition ${
                  ownerExperience === opt.value
                    ? 'border-navy bg-navy text-white'
                    : 'border-gray-200 text-gray-700 hover:border-navy'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {previousOwnerExperience && (
            <div className="rounded-xl bg-gray-50 border border-gray-200 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                {previousSnapshotMonth || 'Previous Snapshot'}
              </p>
              <p className="font-semibold text-navy mt-1">{previousOwnerExperience.label}</p>
              {(previousOwnerExperience.note || previousOwnerExperience.summary) && (
                <p className="text-sm text-gray-600 mt-1">
                  {previousOwnerExperience.note || previousOwnerExperience.summary}
                </p>
              )}
            </div>
          )}

          {ownerExperience && (
            <div className="space-y-4">
              <div
                className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1"
                role="tablist"
                aria-label="Pulse reflection method"
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={ownerExperienceMode === 'notes'}
                  onClick={() => choosePulseMode('notes')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 pointer-coarse:py-2.5 rounded-md text-sm font-medium transition ${
                    ownerExperienceMode === 'notes' ? 'bg-navy text-white' : 'text-gray-600 hover:text-navy'
                  }`}
                >
                  <PenLine size={16} />
                  With my own notes
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={ownerExperienceMode === 'jarvis'}
                  onClick={() => choosePulseMode('jarvis')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 pointer-coarse:py-2.5 rounded-md text-sm font-medium transition ${
                    ownerExperienceMode === 'jarvis' ? 'bg-navy text-white' : 'text-gray-600 hover:text-navy'
                  }`}
                >
                  <Bot size={16} className={ownerExperienceMode === 'jarvis' ? 'text-gold' : ''} />
                  Talk with Ask IF
                </button>
              </div>

              {ownerExperienceMode === 'notes' && (
                <div>
                  <div className="relative">
                    <AutosizeTextarea
                      value={ownerExperienceNote}
                      onChange={(e) => setOwnerExperienceNote(e.target.value)}
                      className="w-full px-4 py-3 pr-14 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent outline-none resize-none"
                      placeholder="What's shaping how you feel about ownership this month?"
                      aria-label="Entrepreneur Pulse reflection"
                    />
                    <MicButton value={ownerExperienceNote} onChange={setOwnerExperienceNote} className="absolute bottom-2 right-2" />
                  </div>
                  {ownerExperienceNote.trim() && !isMeaningfulReflection(ownerExperienceNote) && (
                    <p className="text-xs text-red-600 mt-1">
                      {REFLECTION_TOO_SHORT_HINT}
                    </p>
                  )}
                </div>
              )}

              {ownerExperienceMode === 'jarvis' && (
                <div className="rounded-xl border border-gray-200 overflow-hidden">
                  <div className="max-h-72 overflow-y-auto p-4 space-y-3">
                    {ownerExperienceTranscript.map((message, index) => (
                      <div key={index} className={message.role === 'user' ? 'text-right' : 'text-left'}>
                        <p className={`inline-block max-w-[85%] rounded-2xl px-4 py-3 text-sm whitespace-pre-wrap ${message.role === 'user' ? 'bg-navy text-white' : 'bg-off-white text-gray-800'}`}>
                          {message.content}
                        </p>
                      </div>
                    ))}
                  </div>
                  <form
                    className="border-t border-gray-100 p-3 flex items-end gap-2"
                    onSubmit={(event) => { event.preventDefault(); void sendPulseMessage(); }}
                  >
                    <div className="relative flex-1">
                    <AutosizeTextarea
                      value={pulseChatInput}
                      onChange={(event) => setPulseChatInput(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' && !event.shiftKey) {
                          event.preventDefault();
                          void sendPulseMessage();
                        }
                      }}
                      maxHeight={140}
                      disabled={pulseChatLoading}
                      placeholder="Share your thoughts…"
                      aria-label="Message Ask IF about your Entrepreneur Pulse"
                      name="if-snapshot-talk"
                      autoComplete="off"
                      className="w-full px-4 py-2 pr-12 rounded-xl border border-gray-300 outline-none resize-none"
                    />
                    <MicButton
                      value={pulseChatInput}
                      onChange={setPulseChatInput}
                      className="absolute bottom-2 right-2"
                    />
                    </div>
                    <button type="submit" disabled={!pulseChatInput.trim() || pulseChatLoading} aria-label="Send Pulse message" className="p-3 bg-navy text-white rounded-xl disabled:opacity-50">
                      <Send size={18} />
                    </button>
                  </form>
                  {ownerExperienceTranscript.some((message) => message.role === 'user') && !ownerExperienceSummary && (
                    <button type="button" onClick={() => void concludePulse(ownerExperienceTranscript)} className="m-3 text-sm font-semibold text-navy hover:underline">
                      I'm done — suggest a reflection
                    </button>
                  )}
                </div>
              )}

              {ownerExperienceSummary && (
                <div className="rounded-xl border border-gold/40 bg-gold/10 p-4 space-y-3">
                  <p className="text-sm font-semibold text-navy">Suggested Reflection</p>
                  <p className="text-sm text-gray-700">{ownerExperienceSummary}</p>
                  <div className="flex flex-wrap gap-3 text-sm font-semibold">
                    <button type="button" onClick={() => { setOwnerExperienceNote(ownerExperienceSummary); setOwnerExperienceMode('notes'); }} className="text-navy hover:underline">Use as my note</button>
                    <button type="button" onClick={() => { setOwnerExperienceNote(ownerExperienceSummary); setOwnerExperienceMode('notes'); }} className="text-navy hover:underline">Edit</button>
                    <button type="button" onClick={() => setOwnerExperienceMode('notes')} className="text-gray-600 hover:underline">Keep my own</button>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex flex-col items-end gap-2">
            {!pulseComplete && (
              <p className="text-sm text-gray-500 text-right">
                {ownerExperience
                  ? 'Write a short note about how ownership feels, then continue.'
                  : 'Pick how ownership feels, then write a short note to continue.'}
              </p>
            )}
            <button
              onClick={() => {
                if (!pulseComplete) {
                  toast.error(
                    ownerExperience
                      ? 'Write a short note about how ownership feels, then continue.'
                      : 'Pick how ownership feels, then write a short note to continue.',
                  );
                  return;
                }
                nextFromMacro();
              }}
              aria-disabled={!pulseComplete}
              className={`flex items-center gap-2 px-6 py-3 bg-navy text-white rounded-lg font-semibold hover:bg-navy-dark transition ${
                pulseComplete ? '' : 'opacity-50'
              }`}
            >
              Continue
              <ArrowRight size={20} />
            </button>
          </div>
        </div>
      )}

      {/* Major Prayer Request Step */}
      {currentStep === 'prayer' && (
        <div className="bg-white rounded-2xl p-6 shadow-sm space-y-6">
          <div>
            <h2 className="text-2xl font-bold text-navy flex items-center gap-2">
              <Heart size={22} className="text-gold" />
              Major Prayer Request
            </h2>
            <p className="text-gray-600 mt-1">
              What's the most important thing you'd like your group to be praying about this month?
            </p>
          </div>

          <div className="relative">
            <AutosizeTextarea
              value={majorIssue}
              onChange={(e) => setMajorIssue(e.target.value)}
              maxHeight={220}
              className="w-full px-4 py-3 pr-14 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent outline-none resize-none"
              placeholder="Share what's on your heart..."
            />
            <MicButton value={majorIssue} onChange={setMajorIssue} className="absolute bottom-2 right-2" />
          </div>

          {majorIssue.trim() && (
            <div className="p-3 bg-off-white rounded-lg space-y-2">
              <ShareGroupPicker
                selectedIds={sharePrayerGroupIds}
                onChange={setSharePrayerGroupIds}
                title="Share this prayer with accountability group(s)"
                emptyMessage="Join an accountability group to share this prayer."
              />
              <p className="text-xs text-gray-500">
                Off by default — your prayer stays private unless you choose a group.
              </p>
            </div>
          )}

          <div className="flex justify-between">
            <button onClick={backFromMacro} className="flex items-center gap-2 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition">
              <ArrowLeft size={20} />
              Back
            </button>
            <button onClick={nextFromMacro} className="flex items-center gap-2 px-6 py-3 bg-navy text-white rounded-lg font-semibold hover:bg-navy-dark transition">
              Continue
              <ArrowRight size={20} />
            </button>
          </div>
        </div>
      )}

      {/* Quarterly Definitions Step */}
      {currentStep === 'definitions' && (
        <div className="bg-white rounded-2xl p-6 shadow-sm space-y-6">
          <div>
            <h2 className="text-2xl font-bold text-navy flex items-center gap-2">
              <Compass size={22} />
              Define What Healthy Looks Like
            </h2>
            <p className="text-gray-600 mt-1">
              This is a new season. For each area, describe what "healthy" means for you right now — this is the
              anchor you'll rate against. You can finish a few now and come back later. Finalizing
              without them asks you to confirm first.
            </p>
            {isFirstSnapshot && isFirstDefinitionSetup && (
              <p className="text-sm text-navy bg-gold/10 border border-gold/30 rounded-lg p-3 mt-3">
                Take about 5–10 minutes to set these anchors thoughtfully. You can update them later
                in Snapshot Settings.
              </p>
            )}
            {carriedForward && (
              <p className="text-sm text-navy bg-off-white rounded-lg p-3 mt-3">
                We carried your definitions forward from last season. Edit anything that has changed, or leave them
                as-is.
              </p>
            )}
          </div>

          <div className="space-y-4">
            {categories.filter((cat) => !ratings.get(cat.id)?.skipped).map((cat) => {
              const def = definitions.get(cat.id);
              return (
                <div key={cat.id} className="border border-gray-200 rounded-xl p-4 space-y-3">
                  <div>
                    <h3 className="font-semibold text-navy">{cat.name}</h3>
                    {cat.scripture_reference && (
                      <p className="text-xs text-gray-500 italic">{cat.scripture_reference}</p>
                    )}
                  </div>
                  <div className="relative">
                    <textarea
                      value={def?.healthy_looks_like || ''}
                      onChange={(e) => updateDefinition(cat.id, { healthy_looks_like: e.target.value })}
                      rows={2}
                      className="w-full px-4 py-3 pr-14 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent outline-none resize-none"
                      placeholder={`What does a healthy "${cat.name}" look like for you this season?`}
                    />
                    <MicButton
                      value={def?.healthy_looks_like || ''}
                      onChange={(next) => updateDefinition(cat.id, { healthy_looks_like: next })}
                      className="absolute bottom-2 right-2"
                    />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-3">
            <button onClick={backFromMacro} className="flex items-center justify-center gap-2 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition">
              <ArrowLeft size={20} />
              Back
            </button>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={() => handleSubmit(false)}
                disabled={isSaving}
                className="flex items-center justify-center gap-2 px-6 py-3 border-2 border-navy text-navy rounded-lg font-semibold hover:bg-navy hover:text-white transition disabled:opacity-50"
              >
                Save and finish later
              </button>
              <button
                onClick={nextFromMacro}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-navy text-white rounded-lg font-semibold hover:bg-navy-dark transition"
              >
                Continue
                <ArrowRight size={20} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Foundation Step */}
      {currentStep === 'foundation' && (
        <div className="bg-white rounded-2xl p-6 shadow-sm space-y-6">
          <div>
            <h2 className="text-2xl font-bold text-navy">Foundation</h2>
            <p className="text-gray-600 mt-1">Your purpose statement.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">My Purpose Statement</label>
            <div className="relative">
              <textarea
                value={purposeStatement}
                onChange={(e) => setPurposeStatement(e.target.value)}
                rows={3}
                className="w-full px-4 py-3 pr-14 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent outline-none resize-none"
                placeholder="What is your God-given purpose?"
              />
              <MicButton value={purposeStatement} onChange={setPurposeStatement} className="absolute bottom-2 right-2" />
            </div>
          </div>

          <div className="flex justify-between">
            <button onClick={backFromMacro} className="flex items-center gap-2 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition">
              <ArrowLeft size={20} />
              Back
            </button>
            <button onClick={nextFromMacro} className="flex items-center gap-2 px-6 py-3 bg-navy text-white rounded-lg font-semibold hover:bg-navy-dark transition">
              {hasCommitmentsToReview ? 'Review Grow Goals' : 'Continue to Ratings'}
              <ArrowRight size={20} />
            </button>
          </div>
        </div>
      )}

      {/* Commitments Review Step */}
      {currentStep === 'commitments_review' && (
        <div className="bg-white rounded-2xl p-6 shadow-sm space-y-6">
          <div>
            <h2 className="text-2xl font-bold text-navy">Review Your Grow Goals</h2>
            <p className="text-gray-600 mt-1">How did you do on the Grow Goals you made last month?</p>
          </div>

          <div className="space-y-4">
            {existingCommitments.map((commitment) => {
              const review = commitmentReviews.get(commitment.id);
              return (
                <div key={commitment.id} className="border border-gray-200 rounded-xl p-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <Target className="text-navy mt-1" size={20} />
                    <div className="flex-1">
                      <p className="font-medium text-gray-800">{commitment.description}</p>
                      {commitment.category_name && (
                        <p className="text-sm text-gray-500 mt-1">{commitment.category_name}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => updateCommitmentReview(commitment.id, 'completed')}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition ${
                        review?.status === 'completed' ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <CheckCircle size={16} />
                      Completed
                    </button>
                    <button
                      type="button"
                      onClick={() => updateCommitmentReview(commitment.id, 'in_progress')}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition ${
                        review?.status === 'in_progress' ? 'bg-gold text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <RefreshCw size={16} />
                      Still Working
                    </button>
                    <button
                      type="button"
                      onClick={() => updateCommitmentReview(commitment.id, 'cancelled')}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition ${
                        review?.status === 'cancelled' ? 'bg-gray-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <X size={16} />
                      No Longer Applicable
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex justify-between">
            <button onClick={backFromMacro} className="flex items-center gap-2 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition">
              <ArrowLeft size={20} />
              Back
            </button>
            <button onClick={nextFromMacro} className="flex items-center gap-2 px-6 py-3 bg-navy text-white rounded-lg font-semibold hover:bg-navy-dark transition">
              Continue to Ratings
              <ArrowRight size={20} />
            </button>
          </div>
        </div>
      )}

      {currentStep === 'rating' && (!currentCategory || !currentRating) && (
        <div className="bg-white rounded-2xl p-6 shadow-sm space-y-4">
          <h2 className="text-xl font-bold text-navy">No snapshot areas to rate</h2>
          <p className="text-gray-600 text-sm">
            We could not load the areas for your snapshot. Go back and try again,
            or contact support if this keeps happening.
          </p>
          <button
            onClick={backFromMacro}
            className="flex items-center gap-2 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition"
          >
            <ArrowLeft size={20} />
            Back
          </button>
        </div>
      )}

      {/* Rating Step */}
      {currentStep === 'rating' && currentCategory && currentRating && (
        <div className="space-y-4">
          <div ref={ratingSectionRef} className="bg-white rounded-2xl p-6 shadow-sm space-y-5 scroll-mt-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-xl font-bold text-navy">{currentCategory.name}</h2>
                {currentCategory.scripture_reference && (
                  <p className="text-sm text-gray-500 italic mt-1">{currentCategory.scripture_reference}</p>
                )}
              </div>
              <button
                type="button"
                onClick={toggleSkip}
                className={`flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 pointer-coarse:py-2.5 rounded-lg border transition flex-shrink-0 ${
                  currentRating.skipped
                    ? 'border-navy text-navy bg-off-white'
                    : 'border-gray-200 text-gray-500 hover:border-navy hover:text-navy'
                }`}
              >
                {currentRating.skipped ? (
                  <>
                    <RotateCcw size={16} />
                    Un-skip
                  </>
                ) : (
                  <>
                    <Ban size={16} />
                    Skip / N/A
                  </>
                )}
              </button>
            </div>

            {previousSnapshotMonth && previousRating && (
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
                <span className="font-medium text-gray-600">{previousSnapshotMonth}: </span>
                {previousRating.skipped ? (
                  <span className="text-gray-700">Skipped / N/A</span>
                ) : previousRating.state_label || previousRating.direction_label ? (
                  <span className="inline-flex flex-wrap items-center gap-1.5">
                    <span
                      className={`${stateColor(previousRating.state)} rounded-full px-2 py-0.5 text-xs font-semibold text-white`}
                    >
                      State: {previousRating.state_label}
                    </span>
                    <span aria-hidden="true" className="text-gray-500">•</span>
                    <span
                      className={`${directionColor(previousRating.direction)} rounded-full px-2 py-0.5 text-xs font-semibold text-white`}
                    >
                      Direction: {previousRating.direction_label}
                    </span>
                  </span>
                ) : (
                  <span className="text-gray-700">No State recorded</span>
                )}
              </div>
            )}

            {currentRating.skipped ? (
              <div className="bg-off-white rounded-xl p-6 text-center space-y-2">
                <Ban className="mx-auto text-gray-400" size={28} />
                <p className="font-medium text-gray-700">Marked "not applicable" this month</p>
                <p className="text-sm text-gray-500">
                  This area won't be rated, flagged, or included in your report.
                </p>
                <button
                  onClick={toggleSkip}
                  className="inline-flex items-center gap-1 text-sm font-medium text-navy hover:underline"
                >
                  <RotateCcw size={14} />
                  Rate this area instead
                </button>
              </div>
            ) : (
              <>
                {currentDefinition?.healthy_looks_like && (
                  <div className="bg-off-white rounded-lg p-3 text-sm">
                    <span className="font-medium text-navy">What healthy looks like for you: </span>
                    <span className="text-gray-700">{currentDefinition.healthy_looks_like}</span>
                  </div>
                )}

                {/* State */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">State — where is this area right now?</label>
                  <div
                    className="grid grid-cols-2 sm:grid-cols-4 gap-2"
                    role="radiogroup"
                    aria-label={`State rating for ${currentCategory.name}`}
                  >
                    {STATE_BUTTONS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        role="radio"
                        aria-checked={currentRating.state === opt.value}
                        onClick={() => updateRating({ state: opt.value })}
                        className={`py-3 px-2 rounded-lg border-2 text-sm font-semibold transition ${
                          currentRating.state === opt.value ? opt.active : 'border-gray-200 text-gray-600 hover:border-navy'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Direction */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Direction — which way is it trending?</label>
                  <div
                    className="grid grid-cols-3 gap-2"
                    role="radiogroup"
                    aria-label={`Direction rating for ${currentCategory.name}`}
                  >
                    {DIRECTION_BUTTONS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        role="radio"
                        aria-checked={currentRating.direction === opt.value}
                        onClick={() => updateRating({ direction: opt.value })}
                        className={`py-3 px-2 rounded-lg border-2 text-sm font-semibold transition ${
                          currentRating.direction === opt.value
                            ? `${directionColor(opt.value)} text-white border-transparent`
                            : 'border-gray-200 text-gray-600 hover:border-navy'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Optional secondary perspective */}
                {currentCategory.has_spouse_rating && (
                  <div className="pt-4 border-t border-gray-100 space-y-3">
                    <p className="text-sm font-medium text-gray-700">
                      {secondaryRatingLabel(currentCategory.name)}
                    </p>
                    <div
                      className="grid grid-cols-2 sm:grid-cols-4 gap-2"
                      role="radiogroup"
                      aria-label={`${secondaryRatingLabel(currentCategory.name)} state`}
                    >
                      {STATE_BUTTONS.map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          role="radio"
                          aria-checked={currentRating.spouse_state === opt.value}
                          onClick={() => updateRating({ spouse_state: opt.value })}
                          className={`py-2 px-2 rounded-lg border-2 text-xs font-semibold transition ${
                            currentRating.spouse_state === opt.value ? opt.active : 'border-gray-200 text-gray-600 hover:border-navy'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                    <div
                      className="grid grid-cols-3 gap-2"
                      role="radiogroup"
                      aria-label={`${secondaryRatingLabel(currentCategory.name)} direction`}
                    >
                      {DIRECTION_BUTTONS.map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          role="radio"
                          aria-checked={currentRating.spouse_direction === opt.value}
                          onClick={() => updateRating({ spouse_direction: opt.value })}
                          className={`py-2 px-2 rounded-lg border-2 text-xs font-semibold transition ${
                            currentRating.spouse_direction === opt.value
                              ? `${directionColor(opt.value)} text-white border-transparent`
                              : 'border-gray-200 text-gray-600 hover:border-navy'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Member note is required; Jarvis remains an optional helper. */}
                <div className="pt-4 border-t border-gray-100 space-y-3">
                  <div
                    className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1"
                    role="tablist"
                    aria-label="Reflection method"
                  >
                    <button
                      type="button"
                      role="tab"
                      aria-selected={inputMode === 'notes'}
                      onClick={switchToNotes}
                      disabled={isChatLoading}
                      className={`flex items-center gap-1.5 px-3 py-1.5 pointer-coarse:py-2.5 rounded-md text-sm font-medium transition ${
                        inputMode === 'notes' ? 'bg-navy text-white' : 'text-gray-600 hover:text-navy'
                      } disabled:opacity-50`}
                    >
                      <PenLine size={16} />
                      With my own notes
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={inputMode === 'jarvis'}
                      onClick={switchToJarvis}
                      disabled={isChatLoading}
                      className={`flex items-center gap-1.5 px-3 py-1.5 pointer-coarse:py-2.5 rounded-md text-sm font-medium transition ${
                        inputMode === 'jarvis' ? 'bg-navy text-white' : 'text-gray-600 hover:text-navy'
                      } disabled:opacity-50`}
                    >
                      <Bot size={16} className={inputMode === 'jarvis' ? 'text-gold' : ''} />
                      Talk with Ask IF
                    </button>
                  </div>

                  {inputMode !== 'jarvis' ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {REFLECTION_PROMPT}
                      </label>
                      <div className="relative">
                        <AutosizeTextarea
                          value={currentRating.rating_text}
                          onChange={(e) => updateRating({ rating_text: e.target.value })}
                          className="w-full px-4 py-3 pr-14 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent outline-none resize-none"
                          placeholder={REFLECTION_PROMPT}
                        />
                        <MicButton
                          value={currentRating.rating_text}
                          onChange={(next) => updateRating({ rating_text: next })}
                          className="absolute bottom-2 right-2"
                        />
                      </div>
                      {currentRating.rating_text.trim() && !isMeaningfulReflection(currentRating.rating_text) && (
                        <p className="text-xs text-red-600 mt-1">
                          {REFLECTION_TOO_SHORT_HINT}
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500">
                      Ask IF will ask a few curious questions. The transcript stays separate from
                      your member note.
                    </p>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Optional Jarvis probe */}
          {!currentRating.skipped && inputMode === 'jarvis' && (
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden flex flex-col" style={{ height: '400px' }}>
              <div ref={chatScrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">
                {chatMessages.map((message, index) => (
                  <div key={index} className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    {message.role === 'assistant' && (
                      <div className="w-8 h-8 bg-navy rounded-lg flex items-center justify-center flex-shrink-0">
                        <Bot className="text-gold" size={18} />
                      </div>
                    )}
                    <div
                      className={`max-w-[75%] p-3 rounded-2xl ${
                        message.role === 'user' ? 'bg-navy text-white rounded-br-md' : 'bg-off-white text-gray-800 rounded-bl-md'
                      }`}
                    >
                      <p className="whitespace-pre-wrap text-sm">{message.content}</p>
                    </div>
                    {message.role === 'user' && (
                      <div className="w-8 h-8 bg-gold rounded-lg flex items-center justify-center flex-shrink-0">
                        <UserIcon className="text-white" size={18} />
                      </div>
                    )}
                  </div>
                ))}
                {isChatLoading && (
                  <div className="flex gap-3">
                    <div className="w-8 h-8 bg-navy rounded-lg flex items-center justify-center flex-shrink-0">
                      <Bot className="text-gold" size={18} />
                    </div>
                    <div className="bg-off-white p-3 rounded-2xl rounded-bl-md">
                      <div className="flex gap-1">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </div>
                )}
                {chatError && (
                  <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                    <p>{chatError}</p>
                    <button
                      type="button"
                      onClick={retryConversation}
                      disabled={isChatLoading}
                      className="mt-2 inline-flex items-center gap-1 font-semibold text-navy hover:underline disabled:opacity-50"
                    >
                      <RefreshCw size={14} />
                      Retry
                    </button>
                  </div>
                )}
              </div>

              <div className="p-4 border-t border-gray-100">
                {chatConcluded ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm text-navy bg-gold/10 rounded-xl px-4 py-3">
                      <CheckCircle size={18} className="text-gold flex-shrink-0" />
                      <span>You've wrapped up this area — your transcript and Suggested Reflection are saved.</span>
                    </div>
                    <div className="flex items-center justify-end">
                      <button onClick={switchToNotes} disabled={isChatLoading} className="text-sm font-medium text-navy hover:underline disabled:opacity-50">
                        Edit my notes instead
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        sendMessage();
                      }}
                      className="flex gap-3"
                    >
                      <div className="relative flex-1">
                      <AutosizeTextarea
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' && !event.shiftKey) {
                            event.preventDefault();
                            sendMessage();
                          }
                        }}
                        maxHeight={140}
                        placeholder="Share your thoughts..."
                        name="if-snapshot-talk"
                        autoComplete="off"
                        className="w-full px-4 py-2 pr-12 rounded-xl border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent outline-none text-sm resize-none"
                        disabled={isChatLoading}
                      />
                      <MicButton
                        value={chatInput}
                        onChange={setChatInput}
                        className="absolute bottom-2 right-2"
                      />
                      </div>
                      <button
                        type="submit"
                        disabled={!chatInput.trim() || isChatLoading}
                        className="px-4 py-2 bg-navy text-white rounded-xl hover:bg-navy-dark transition disabled:opacity-50"
                        aria-label="Send message"
                      >
                        <Send size={18} />
                      </button>
                    </form>
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-xs text-gray-400">Your transcript is saved separately.</span>
                      <div className="flex items-center gap-4">
                        {chatMessages.some((m) => m.role === 'user') && (
                          <button onClick={finishConversation} disabled={isChatLoading} className="flex items-center gap-1 text-sm font-medium text-navy hover:underline disabled:opacity-50">
                            <CheckCircle size={15} />
                            I'm done
                          </button>
                        )}
                        <button onClick={switchToNotes} disabled={isChatLoading} className="text-sm font-medium text-navy hover:underline disabled:opacity-50">
                          Write my own notes instead
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {!currentRating.skipped && currentRating.description && (
            <div className="rounded-xl border border-gold/40 bg-gold/10 p-4 space-y-3">
              <p className="text-sm font-semibold text-navy">Suggested Reflection</p>
              <p className="text-sm text-gray-700">{currentRating.description}</p>
              <div className="flex flex-wrap gap-3 text-sm font-semibold">
                <button type="button" onClick={() => { updateRating({ rating_text: currentRating.description, reflection_mode: 'notes' }); setInputMode('notes'); }} className="text-navy hover:underline">
                  Use as my note
                </button>
                <button type="button" onClick={() => { updateRating({ rating_text: currentRating.description, reflection_mode: 'notes' }); setInputMode('notes'); }} className="text-navy hover:underline">
                  Edit
                </button>
                <button type="button" onClick={() => { updateRating({ reflection_mode: 'notes' }); setInputMode('notes'); }} className="text-gray-600 hover:underline">
                  Keep my own
                </button>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between items-center">
            <button
              onClick={ratingBack}
              disabled={isChatLoading}
              aria-disabled={isChatLoading}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-navy transition disabled:opacity-50"
            >
              <ArrowLeft size={20} />
              Back
            </button>
            <span className="text-sm text-gray-500" aria-live="polite">
              {isChatLoading
                ? `Ask IF is responding. Navigation is disabled for area ${currentCategoryIndex + 1} of ${categories.length}.`
                : `${currentCategoryIndex + 1} of ${categories.length} areas`}
            </span>
            <button
              onClick={ratingNext}
              aria-disabled={
                isChatLoading ||
                (
                  !currentRating.skipped &&
                  (
                    currentRating.state == null ||
                    currentRating.direction == null ||
                    !(
                      (currentRating.reflection_mode === 'notes' && isMeaningfulReflection(currentRating.rating_text))
                      || (
                        currentRating.reflection_mode === 'jarvis'
                        && isMeaningfulReflection(currentRating.description)
                        && currentRating.conversation_transcript.some((message) => message.role === 'user')
                      )
                    )
                  )
                )
              }
              disabled={
                isChatLoading ||
                (
                  !currentRating.skipped &&
                  (
                    currentRating.state == null ||
                    currentRating.direction == null ||
                    !(
                      (currentRating.reflection_mode === 'notes' && isMeaningfulReflection(currentRating.rating_text))
                      || (
                        currentRating.reflection_mode === 'jarvis'
                        && isMeaningfulReflection(currentRating.description)
                        && currentRating.conversation_transcript.some((message) => message.role === 'user')
                      )
                    )
                  )
                )
              }
              className="flex items-center gap-2 px-4 py-2 bg-navy text-white rounded-lg font-semibold hover:bg-navy-dark transition disabled:opacity-50"
            >
              {currentCategoryIndex < categories.length - 1 ? 'Next Area' : 'Continue'}
              <ArrowRight size={20} />
            </button>
          </div>
        </div>
      )}

      {/* New Commitments Step */}
      {currentStep === 'commitments_new' && (
        <div className="bg-white rounded-2xl p-6 shadow-sm space-y-6">
          {goalModal && (
            <div className="fixed inset-0 z-50 bg-black/40 p-4 flex items-center justify-center">
              <div role="dialog" aria-modal="true" aria-labelledby="goal-modal-title" className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h2 id="goal-modal-title" className="text-xl font-bold text-navy">Configure Grow Goal</h2>
                  <button type="button" onClick={() => setGoalModal(null)} aria-label="Close Grow Goal modal"><X size={20} /></button>
                </div>
                <label className="block text-sm font-medium text-gray-700">
                  Description
                  <AutosizeTextarea value={goalModal.description} onChange={(event) => setGoalModal({ ...goalModal, description: event.target.value })} className="mt-1 w-full px-4 py-3 rounded-lg border border-gray-300 resize-none" />
                </label>
                <div className="grid sm:grid-cols-2 gap-4">
                  <label className="text-sm font-medium text-gray-700">
                    Area
                    <select value={goalModal.category ?? ''} onChange={(event) => setGoalModal({ ...goalModal, category: event.target.value ? Number(event.target.value) : null })} className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300">
                      <option value="">No specific area</option>
                      {categories.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}
                    </select>
                  </label>
                  <label className="text-sm font-medium text-gray-700">
                    Frequency
                    <select value={goalModal.frequency} onChange={(event) => setGoalModal({ ...goalModal, frequency: event.target.value as NewCommitmentData['frequency'] })} className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300">
                      {(['one_time', 'daily', 'weekly', 'biweekly', 'monthly'] as const).map((frequency) => <option key={frequency} value={frequency}>{getFrequencyLabel(frequency)}</option>)}
                    </select>
                  </label>
                  <label className="text-sm font-medium text-gray-700">
                    Target count
                    <input type="number" min={1} max={31} value={goalModal.target_count} onChange={(event) => setGoalModal({ ...goalModal, target_count: Math.max(1, Number(event.target.value) || 1) })} className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300" />
                  </label>
                  <label className="text-sm font-medium text-gray-700">
                    Scheduling
                    <select value={goalModal.scheduling_mode} onChange={(event) => setGoalModal({ ...goalModal, scheduling_mode: event.target.value as NewCommitmentData['scheduling_mode'] })} className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300">
                      <option value="specific">Specific days</option>
                      <option value="flexible">Flexible</option>
                    </select>
                  </label>
                </div>
                <ShareGroupPicker
                  selectedIds={goalModal.shared_group_ids}
                  onChange={(ids) => setGoalModal({ ...goalModal, shared_group_ids: ids })}
                />
                <div className="flex justify-end gap-3">
                  <button type="button" onClick={() => setGoalModal(null)} className="px-4 py-2 border border-gray-300 rounded-lg">Cancel</button>
                  <button type="button" onClick={confirmGoal} disabled={!goalModal.description.trim()} className="px-4 py-2 bg-navy text-white rounded-lg font-semibold disabled:opacity-50">Add Grow Goal</button>
                </div>
              </div>
            </div>
          )}
          <div>
            <h2 className="text-2xl font-bold text-navy">Make New Grow Goals</h2>
            <p className="text-gray-600 mt-1">
              Ask IF reviewed your snapshot and can help you turn the weak spots into concrete goals.
            </p>
            <p className="text-sm text-gray-500 mt-1">
              What change are you committing to before the next forum?
            </p>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-navy">Suggested from your Snapshot</h3>
              <button type="button" onClick={() => void loadGoalSuggestions()} disabled={suggestionsLoading} className="text-sm text-navy hover:underline disabled:opacity-50">
                Refresh
              </button>
            </div>
            {suggestionsLoading ? (
              <p className="text-sm text-gray-500">Preparing suggestions…</p>
            ) : goalSuggestions.length > 0 ? (
              <div className="grid gap-3">
                {goalSuggestions.map((suggestion, index) => (
                  <div key={`${suggestion.description}-${index}`} className="rounded-xl border border-gold/40 bg-gold/10 p-4">
                    <p className="font-medium text-navy">{suggestion.description}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {[suggestion.category_name, getFrequencyLabel(suggestion.frequency)].filter(Boolean).join(' · ')}
                    </p>
                    <button type="button" onClick={() => openGoalSuggestion(suggestion)} className="mt-3 inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-navy text-white text-sm font-semibold">
                      <Plus size={16} /> Start Grow Goal
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">Add a goal manually or use the optional coaching chat.</p>
            )}
          </div>

          {/* Jarvis grow-goal coaching chat */}
          <div className="border border-gray-200 rounded-xl overflow-hidden flex flex-col" style={{ height: '420px' }}>
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {coachMessages.map((message, index) => {
                return (
                  <div key={index} className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    {message.role === 'assistant' && (
                      <div className="w-8 h-8 bg-navy rounded-lg flex items-center justify-center flex-shrink-0">
                        <Bot className="text-gold" size={18} />
                      </div>
                    )}
                    <div className={`max-w-[80%] space-y-2 ${message.role === 'user' ? 'items-end' : ''}`}>
                      <div
                        className={`p-3 rounded-2xl ${
                          message.role === 'user' ? 'bg-navy text-white rounded-br-md' : 'bg-off-white text-gray-800 rounded-bl-md'
                        }`}
                      >
                        <p className="whitespace-pre-wrap text-sm">{message.content}</p>
                      </div>
                    </div>
                    {message.role === 'user' && (
                      <div className="w-8 h-8 bg-gold rounded-lg flex items-center justify-center flex-shrink-0">
                        <UserIcon className="text-white" size={18} />
                      </div>
                    )}
                  </div>
                );
              })}
              {isCoachLoading && (
                <div className="flex gap-3">
                  <div className="w-8 h-8 bg-navy rounded-lg flex items-center justify-center flex-shrink-0">
                    <Bot className="text-gold" size={18} />
                  </div>
                  <div className="bg-off-white p-3 rounded-2xl rounded-bl-md">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              )}
              <div ref={coachEndRef} />
            </div>

            <div className="p-4 border-t border-gray-100">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  sendCoachMessage();
                }}
                className="flex gap-3"
              >
                <div className="flex-1 relative">
                  <input
                    type="text"
                    value={coachInput}
                    onChange={(e) => setCoachInput(e.target.value)}
                    placeholder="Ask IF or refine a goal..."
                    className="w-full px-4 py-2 pr-12 rounded-xl border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent outline-none text-sm"
                    disabled={isCoachLoading}
                  />
                  <MicButton value={coachInput} onChange={setCoachInput} className="absolute top-1/2 -translate-y-1/2 right-2" />
                </div>
                <button
                  type="submit"
                  disabled={!coachInput.trim() || isCoachLoading}
                  className="px-4 py-2 bg-navy text-white rounded-xl hover:bg-navy-dark transition disabled:opacity-50"
                  aria-label="Send message"
                >
                  <Send size={18} />
                </button>
              </form>
              <p className="text-xs text-gray-400 mt-2">Optional coaching chat for refining your goal ideas.</p>
            </div>
          </div>

          <div className="space-y-4">
            {newCommitments.map((commitment, index) => (
              <div key={index} className="border border-gray-200 rounded-xl p-4 space-y-4">
                <div className="flex items-start gap-2">
                  <div className="flex-1 relative">
                    <AutosizeTextarea
                      value={commitment.description}
                      onChange={(e) => updateNewCommitment(index, { description: e.target.value })}
                      className="w-full px-4 py-3 pr-14 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent outline-none resize-none"
                      placeholder="I commit to..."
                    />
                    <MicButton
                      value={commitment.description}
                      onChange={(next) => updateNewCommitment(index, { description: next })}
                      className="absolute bottom-2 right-2"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeNewCommitment(index)}
                    className="p-2 text-gray-400 hover:text-red-500 transition"
                    aria-label="Remove Grow Goal"
                  >
                    <X size={20} />
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Related Area</label>
                    <select
                      value={commitment.category || ''}
                      onChange={(e) => updateNewCommitment(index, { category: e.target.value ? parseInt(e.target.value) : null })}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent outline-none"
                    >
                      <option value="">Select category</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Frequency</label>
                    <div className="flex gap-2">
                      {(['one_time', 'daily', 'weekly', 'monthly'] as const).map((freq) => (
                        <button
                          key={freq}
                          type="button"
                          onClick={() => updateNewCommitment(index, { frequency: freq })}
                          className={`flex-1 py-2 px-2 rounded-lg text-xs font-medium transition ${
                            commitment.frequency === freq ? 'bg-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {getFrequencyLabel(freq)}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="p-3 bg-off-white rounded-lg">
                  <ShareGroupPicker
                    selectedIds={commitment.shared_group_ids}
                    onChange={(ids) => updateNewCommitment(index, { shared_group_ids: ids })}
                  />
                </div>
              </div>
            ))}

            <button
              type="button"
              onClick={addNewCommitment}
              className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:border-navy hover:text-navy transition"
            >
              <Plus size={20} />
              Add Grow Goal
            </button>
          </div>

          <div className="flex justify-between">
            <button onClick={backFromMacro} className="flex items-center gap-2 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition">
              <ArrowLeft size={20} />
              Back
            </button>
            <button onClick={nextFromMacro} className="flex items-center gap-2 px-6 py-3 bg-navy text-white rounded-lg font-semibold hover:bg-navy-dark transition">
              Continue
              <ArrowRight size={20} />
            </button>
          </div>
        </div>
      )}

      {/* Most Important Topic Step */}
      {currentStep === 'most_important' && (
        <div className="bg-white rounded-2xl p-6 shadow-sm space-y-6">
          <div>
            <h2 className="text-2xl font-bold text-navy flex items-center gap-2">
              <Star size={22} className="text-gold" />
              What's most important right now?
            </h2>
            <p className="text-gray-600 mt-1">
              Of everything you reflected on, which area would be most valuable to focus on with your group this month?
            </p>
          </div>

          <div className="space-y-3">
            {categories.filter((cat) => !ratings.get(cat.id)?.skipped).map((cat) => {
              const r = ratings.get(cat.id);
              const selected = mostImportantCategory === cat.id;
              const brief = r?.rating_text || r?.description || definitions.get(cat.id)?.healthy_looks_like || '';
              return (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setMostImportantCategory(cat.id)}
                  className={`w-full text-left p-4 rounded-xl border-2 transition ${
                    selected ? 'border-navy bg-off-white' : 'border-gray-200 hover:border-navy'
                  }`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-semibold text-navy">{cat.name}</span>
                    <div className="flex items-center gap-2 text-xs">
                      {r?.state != null && (
                        <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 font-medium">
                          {STATE_LABELS[r.state]}
                        </span>
                      )}
                      {r?.direction != null && (
                        <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 font-medium">
                          {DIRECTION_LABELS[r.direction]}
                        </span>
                      )}
                    </div>
                  </div>
                  {brief && <p className="text-sm text-gray-500 mt-1 line-clamp-2">{brief}</p>}
                </button>
              );
            })}
          </div>

          <div className="flex justify-between">
            <button onClick={backFromMacro} className="flex items-center gap-2 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition">
              <ArrowLeft size={20} />
              Back
            </button>
            <button onClick={nextFromMacro} className="flex items-center gap-2 px-6 py-3 bg-navy text-white rounded-lg font-semibold hover:bg-navy-dark transition">
              Review &amp; Submit
              <ArrowRight size={20} />
            </button>
          </div>
        </div>
      )}

      {/* Review Step */}
      {currentStep === 'review' && (
        <div className="space-y-6">
          <div className="bg-white rounded-2xl p-6 shadow-sm space-y-6">
            <h2 className="text-2xl font-bold text-navy">Review Your Snapshot</h2>

            {/* Header: Entrepreneur Pulse */}
            {ownerExperience && (
              <div className="bg-off-white rounded-lg p-4">
                <span className="text-sm font-medium text-gray-500">Entrepreneur Pulse: </span>
                <span className="font-semibold text-navy">
                  {OWNER_EXPERIENCE_OPTIONS.find((o) => o.value === ownerExperience)?.label}
                </span>
                {ownerExperienceNote && <p className="text-sm text-gray-600 mt-1">{ownerExperienceNote}</p>}
              </div>
            )}

            {/* Forum-day reading order: most-important → prayer → flagged areas,
                then the full per-category ratings below. */}
            {/* 1. Most important topic */}
            {mostImportantCategory != null && (
              <div>
                <h3 className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <Star size={18} className="text-gold" />
                  Most Important Right Now
                </h3>
                <p className="bg-off-white rounded-lg p-4 font-semibold text-navy">
                  {categories.find((c) => c.id === mostImportantCategory)?.name}
                </p>
              </div>
            )}

            {/* 2. Major prayer request */}
            {majorIssue && (
              <div>
                <h3 className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <Heart size={18} className="text-gold" />
                  Major Prayer Request
                </h3>
                <p className="bg-gold/10 rounded-lg p-4 text-gray-800 whitespace-pre-wrap">{majorIssue}</p>
              </div>
            )}

            {/* 3. Flagged areas ("Needs attention" / "Celebrate") surfaced up top */}
            {(() => {
              const flagged = categories.filter((cat) => {
                const r = ratings.get(cat.id);
                if (r?.skipped) return false;
                return isAttention(r?.state, r?.direction) || isCelebrate(r?.state, r?.direction);
              });
              if (flagged.length === 0) return null;
              return (
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Areas to Discuss</h3>
                  <div className="grid gap-2">
                    {flagged.map((category) => {
                      const rating = ratings.get(category.id);
                      const attention = isAttention(rating?.state, rating?.direction);
                      const celebrate = isCelebrate(rating?.state, rating?.direction);
                      return (
                        <div
                          key={category.id}
                          className={`flex items-center justify-between gap-3 p-3 rounded-lg ${
                            attention ? 'bg-red-50' : 'bg-green-50'
                          }`}
                        >
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-gray-800">{category.name}</span>
                            {attention && (
                              <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                                <AlertTriangle size={12} />
                                Needs attention
                              </span>
                            )}
                            {celebrate && (
                              <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                                <Star size={12} />
                                Celebrate
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-xs">
                            {rating?.state != null && (
                              <span className="px-2 py-0.5 rounded-full bg-white border border-gray-200 text-gray-700 font-medium">
                                {STATE_LABELS[rating.state]}
                              </span>
                            )}
                            {rating?.direction != null && (
                              <span className="px-2 py-0.5 rounded-full bg-white border border-gray-200 text-gray-700 font-medium">
                                {DIRECTION_LABELS[rating.direction]}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* 4. Full per-category ratings (all areas, flagged still sorted on top) */}
            <div>
              <h3 className="font-semibold text-gray-700 mb-2">All Ratings</h3>
              {profileSectionsHidden && (
                <p className="text-xs text-gray-400 mb-3">
                  Married or have kids? Update your profile to include the Marriage and
                  Parenting &amp; Children sections.
                </p>
              )}
              <div className="grid gap-3">
                {[...categories]
                  .sort((a, b) => {
                    const ra = ratings.get(a.id);
                    const rb = ratings.get(b.id);
                    return (
                      ratingSortKey(ra?.state, ra?.direction, ra?.skipped) -
                      ratingSortKey(rb?.state, rb?.direction, rb?.skipped)
                    );
                  })
                  .map((category) => {
                    const rating = ratings.get(category.id);
                    if (rating?.skipped) {
                      return (
                        <div key={category.id} className="flex items-center justify-between p-3 rounded-lg text-gray-400">
                          <span className="text-sm">{category.name}</span>
                          <span className="inline-flex items-center gap-1 text-xs font-medium">
                            <Ban size={12} />
                            Skipped this month
                          </span>
                        </div>
                      );
                    }
                    const attention = isAttention(rating?.state, rating?.direction);
                    const celebrate = isCelebrate(rating?.state, rating?.direction);
                    return (
                      <div key={category.id} className="p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-gray-800">{category.name}</span>
                            {attention && (
                              <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                                <AlertTriangle size={12} />
                                Needs attention
                              </span>
                            )}
                            {celebrate && (
                              <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                                <Star size={12} />
                                Celebrate
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-xs">
                            {rating?.state != null && (
                              <span className="px-2 py-0.5 rounded-full bg-white border border-gray-200 text-gray-700 font-medium">
                                {STATE_LABELS[rating.state]}
                              </span>
                            )}
                            {rating?.direction != null && (
                              <span className="px-2 py-0.5 rounded-full bg-white border border-gray-200 text-gray-700 font-medium">
                                {DIRECTION_LABELS[rating.direction]}
                              </span>
                            )}
                          </div>
                        </div>
                        {rating?.rating_text && <p className="text-sm text-gray-500 mt-1 line-clamp-2">{rating.rating_text}</p>}
                      </div>
                    );
                  })}
              </div>
            </div>

            {/* New commitments */}
            {newCommitments.filter((c) => c.description.trim()).length > 0 && (
              <div>
                <h3 className="font-semibold text-gray-700 mb-2">New Grow Goals</h3>
                <div className="grid gap-2">
                  {newCommitments
                    .filter((c) => c.description.trim())
                    .map((commitment, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-off-white rounded-lg">
                        <span className="text-gray-800">{commitment.description}</span>
                        <span className="text-sm text-gray-500">{getFrequencyLabel(commitment.frequency)}</span>
                      </div>
                    ))}
                </div>
              </div>
            )}
            <div className="pt-2 border-t border-gray-100">
              <ShareGroupPicker
                selectedIds={shareSnapshotGroupIds}
                onChange={setShareSnapshotGroupIds}
                title="Share this snapshot with accountability group(s)"
                emptyMessage="Join an accountability group to share this snapshot."
              />
              <p className="text-xs text-gray-500 mt-2">
                Sharing here shows your latest finalized snapshot to those groups.
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <button onClick={backFromMacro} className="flex items-center justify-center gap-2 px-4 py-3 text-gray-600 hover:text-navy transition">
              <ArrowLeft size={20} />
              Back to Edit
            </button>
            <div className="flex-1 flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => handleSubmit(false)}
                disabled={isSaving}
                className="flex-1 py-3 px-6 border-2 border-navy text-navy rounded-lg font-semibold hover:bg-navy hover:text-white transition disabled:opacity-50"
              >
                Save as Draft
              </button>
              <button
                onClick={() => handleSubmit(true)}
                disabled={isSaving}
                className="flex-1 py-3 px-6 bg-navy text-white rounded-lg font-semibold hover:bg-navy-dark transition disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Save size={20} />
                {isSaving ? 'Saving...' : 'Finalize & Share'}
              </button>
            </div>
          </div>
        </div>
      )}
      <ConfirmModal
        isOpen={confirmSkipBenchmarks}
        onCancel={() => setConfirmSkipBenchmarks(false)}
        onConfirm={() => {
          setConfirmSkipBenchmarks(false);
          void handleSubmit(true, true);
        }}
        title="Continue without benchmarks?"
        message="Some areas still don’t say what healthy looks like. You can go back and set those, or finalize this Snapshot without them."
        confirmLabel="Finalize anyway"
        variant="primary"
      />
    </div>
  );
}
